import "@fontsource/inter";
import "@fontsource/inter/600.css";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import Skeleton from "@mui/material/Skeleton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import {
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Title,
} from "chart.js";
import moment from "moment";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { SiSlack } from "react-icons/si";
import { useSelector } from "react-redux";
import styled from "styled-components";

import useAuth from "../../../../../hooks/useAuth";
import useCurrency from "../../../../../hooks/useCurrency";
import useNearViewport from "../../../../../hooks/useNearViewport";
import { storeAnalyticsSelector } from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";
import { productThumbnailUrl } from "../../../../../utils/productImage";
import {
  VELOCITY_TOOL_EXTRA_DAYS,
  decisionHasActiveDeal,
  getIrisPpcState,
  isAtomicUser,
  isDoneForYouDecision,
  isFionaDecision,
  isIrisPpcDecision,
  isRossRevertDecision,
} from "../../constants";
import FionaInProgressChip from "../AgentRecommendations/fiona/FionaInProgressChip";
import { getFionaTab } from "../AgentRecommendations/fiona/fionaRules";
import RankTrackingModalPopup from "../StoreProductDetails/BusinessReportAdvanced/RankTrackingModal";

import { CommentableHeader } from "./CommentableHeader";
import { DailyMetrics } from "./DailyMetrics";
import { InventoryDetails } from "./InventoryDetails";
import IrisPpcCard from "./IrisPpcCard";
import MultiDecisionAccordion from "./MultiDecisionAccordion";
import ReginaDecisionCard from "./ReginaDecisionCard";
import RossDecisionCard from "./RossDecisionCard";
import RuleEngineDecision from "./RuleEngineDecision";
import SamDecisionCard from "./SamDecisionCard";
import { UnitEconomics } from "./UnitEconomics";
import UpcomingDaysTab from "./UpcomingDaysTab";
import VelocityWindowSummary from "./VelocityWindowSummary";
import { WeightedRankSeasonalityTooltip } from "./WeightedRankSeasonalityTooltip";
import { getGridScroller, scrollGridTo } from "./gridScroller";
import {
  UPCOMING_HANDLE_FIELD,
  UPCOMING_TAB_WIDTH,
  hasSeenUpcomingDays,
  markUpcomingDaysSeen,
  onUpcomingDaysSeen,
  shouldPulse,
  sumUpcomingTargets,
} from "./upcomingDays";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

// Sparkline component for weighted rank trend visualization
// Shows a trend line segment that connects across cells with gradient fill
const WeightedRankSparkline = ({
  prevValue,
  currentValue,
  nextValue,
  allValues,
}) => {
  const width = 100;
  const height = 52;
  const paddingTop = 8;
  const paddingBottom = 18; // More padding at bottom to keep line visible

  // Filter out zero/null values for min/max calculation across all values
  const validValues = allValues.filter((v) => v && v > 0);
  if (validValues.length < 2) return null;

  const minVal = Math.min(...validValues);
  const maxVal = Math.max(...validValues);
  const range = maxVal - minVal || 1;

  // Helper to calculate Y position (lower rank = higher position on graph)
  const getY = (val) => {
    if (!val || val <= 0) return height / 2;
    return (
      paddingTop +
      ((val - minVal) / range) * (height - paddingTop - paddingBottom)
    );
  };

  const prevY = prevValue ? getY(prevValue) : null;
  const currentY = getY(currentValue);
  const nextY = nextValue ? getY(nextValue) : null;

  // Calculate color based on position in range (gradient from green to red)
  // Lower rank = better = green, Higher rank = worse = red
  const normalizedPosition = (currentValue - minVal) / range; // 0 = best (green), 1 = worst (red)

  // Interpolate between green and red based on position
  // Green: rgb(110, 231, 183) = #6EE7B7
  // Yellow: rgb(253, 224, 71) = #FDE047 (midpoint)
  // Red: rgb(252, 165, 165) = #FCA5A5
  const getGradientColor = (t) => {
    // Clamp t between 0 and 1
    const clampedT = Math.max(0, Math.min(1, t));

    if (clampedT <= 0.5) {
      // Green to Yellow (0 to 0.5)
      const localT = clampedT * 2;
      const r = Math.round(110 + (253 - 110) * localT);
      const g = Math.round(231 + (224 - 231) * localT);
      const b = Math.round(183 + (71 - 183) * localT);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Yellow to Red (0.5 to 1)
      const localT = (clampedT - 0.5) * 2;
      const r = Math.round(253 + (252 - 253) * localT);
      const g = Math.round(224 + (165 - 224) * localT);
      const b = Math.round(71 + (165 - 71) * localT);
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  const lineColor = getGradientColor(normalizedPosition);
  const gradientId = `gradient-${Math.random().toString(36).substr(2, 9)}`;

  // Build path segments using cubic bezier for smoother curves
  let linePath = "";
  let areaPath = "";

  // Calculate entry and exit points
  const entryY = prevY !== null ? (prevY + currentY) / 2 : currentY;
  const exitY = nextY !== null ? (currentY + nextY) / 2 : currentY;

  // Create smooth curve through the cell
  if (prevY !== null && nextY !== null) {
    // Middle cell - smooth S-curve
    linePath = `M 0 ${entryY} C ${width * 0.3} ${currentY}, ${
      width * 0.7
    } ${currentY}, ${width} ${exitY}`;
  } else if (prevY !== null) {
    // Last cell
    linePath = `M 0 ${entryY} Q ${
      width * 0.5
    } ${currentY}, ${width} ${currentY}`;
  } else if (nextY !== null) {
    // First cell
    linePath = `M 0 ${currentY} Q ${
      width * 0.5
    } ${currentY}, ${width} ${exitY}`;
  } else {
    // Single cell
    linePath = `M 0 ${currentY} L ${width} ${currentY}`;
  }

  // Create area fill path (same as line but closes to bottom)
  areaPath = linePath + ` L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
      }}
    >
      <defs>
        {/* Gradient for area fill */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Gradient fill area */}
      <path d={areaPath} fill={`url(#${gradientId})`} />

      {/* Subtle glow effect */}
      <path
        d={linePath}
        fill="none"
        stroke={lineColor}
        strokeWidth={4}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.2}
      />

      {/* Main line */}
      <path
        d={linePath}
        fill="none"
        stroke={lineColor}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const HistoricalSVGraph = ({ historicalData, term }) => {
  if (!historicalData || Object.keys(historicalData).length === 0) {
    return null;
  }

  const sortedDates = Object.keys(historicalData).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  const latestDate = sortedDates[sortedDates.length - 1];
  const latestSv = historicalData[latestDate];
  const [year, month, day] = latestDate.split("-");
  const formattedDate = `${year.slice(-2)}-${month}-${day}`;

  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  const thisYearData = {};
  const lastYearData = {};

  Object.entries(historicalData).forEach(([date, value]) => {
    const entryYear = new Date(date).getFullYear();
    const entryMonthDay = date.substring(5);
    if (entryYear === currentYear) {
      thisYearData[entryMonthDay] = value;
    } else if (entryYear === lastYear) {
      lastYearData[entryMonthDay] = value;
    }
  });

  const allLabels = [
    ...Object.keys(thisYearData),
    ...Object.keys(lastYearData),
  ];
  const uniqueLabels = [...new Set(allLabels)].sort();

  const monthAbbreviations = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const chartData = {
    labels: uniqueLabels.map((label) => {
      const [month, day] = label.split("-");
      return `${monthAbbreviations[parseInt(month, 10) - 1]}-${day}`;
    }),
    datasets: [
      {
        label: `'${String(currentYear).slice(-2)}`,
        data: uniqueLabels.map((label) => thisYearData[label] || null),
        borderColor: "#b366ff",
        backgroundColor: "rgba(179, 102, 255, 0.5)",
        spanGaps: true,
      },
      {
        label: `'${String(lastYear).slice(-2)}`,
        data: uniqueLabels.map((label) => lastYearData[label] || null),
        borderColor: "#b366ff",
        backgroundColor: "rgba(179, 102, 255, 0.5)",
        borderDash: [5, 5],
        spanGaps: true,
      },
    ],
  };

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString();
            }
            return label;
          },
          labelColor: (context) => {
            const dataset = context.chart.data.datasets[context.datasetIndex];
            const isLastYear = !!dataset.borderDash;
            return {
              borderColor: dataset.borderColor,
              backgroundColor: isLastYear
                ? "rgba(0,0,0,0)"
                : dataset.borderColor,
              borderWidth: 2,
              borderRadius: 0,
            };
          },
        },
      },
    },
    scales: {
      x: {
        display: true, // Show x-axis labels
        ticks: {
          color: "white", // font color
        },
        grid: {
          color: "rgba(255, 255, 255, 0.2)",
        },
      },
      y: {
        display: true, // Show y-axis labels
        ticks: {
          color: "white", // font color
        },
        grid: {
          color: "rgba(255, 255, 255, 0.2)",
        },
      },
    },
    elements: {
      point: {
        radius: 0, // Hide points on the line
      },
    },
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        left: 10,
        right: 10,
        bottom: 10,
      },
    },
    backgroundColor: "#4A4A4A",
  };

  return (
    <div
      style={{
        width: "350px",
        backgroundColor: "#4A4A4A",
        padding: "10px",
        borderRadius: "4px",
        color: "white",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div style={{ paddingBottom: "5px" }}>
        {formattedDate} weekly SV for "{term}" -{" "}
        <strong>{latestSv.toLocaleString()}</strong>
      </div>
      <div style={{ paddingBottom: "5px" }}>Historical values:</div>
      <div style={{ height: "150px" }}>
        <Line options={options} data={chartData} />
      </div>
    </div>
  );
};

// BSR Historical Graph Component
// Uses bottom category scale/line and shows top category as number-only reference.
const BSRHistoricalGraph = ({ bsrData, categories }) => {
  if (!bsrData || !categories || categories.length === 0) {
    return null;
  }

  const topCategory = categories[0];
  const bottomCategory = categories[categories.length - 1];
  const topCategoryId = String(topCategory?.catId);
  const bottomCategoryId = String(bottomCategory?.catId);

  const topCategoryData = bsrData[topCategoryId] || [];
  const bottomCategoryData = bsrData[bottomCategoryId] || [];
  if (bottomCategoryData.length === 0) return null;

  const topDateRankMap = {};
  topCategoryData.forEach((d) => {
    const dateStr = d.time?.split("T")[0];
    if (dateStr) {
      topDateRankMap[dateStr] = d.salesRank;
    }
  });

  // Top category number-only reference
  const topLatestEntry = [...topCategoryData].sort(
    (a, b) => new Date(b.time) - new Date(a.time)
  )[0];
  const topLatestRank = topLatestEntry?.salesRank;

  // Bottom category drives the visualized series and axis scale
  const sortedBottom = [...bottomCategoryData].sort(
    (a, b) => new Date(a.time) - new Date(b.time)
  );
  const bottomLatestEntry = [...bottomCategoryData].sort(
    (a, b) => new Date(b.time) - new Date(a.time)
  )[0];
  const bottomLatestRank = bottomLatestEntry?.salesRank;
  const sortedDates = sortedBottom
    .map((d) => d.time?.split("T")[0])
    .filter(Boolean);

  const bottomRanks = sortedBottom
    .map((d) => d.salesRank)
    .filter((v) => typeof v === "number");
  const minRank = Math.min(...bottomRanks);
  const maxRank = Math.max(...bottomRanks);
  const padding = Math.max((maxRank - minRank) * 0.08, 1);
  const yMin = Math.max(1, minRank - padding);
  const yMax = maxRank + padding;

  const chartData = {
    labels: sortedDates.map((d) => {
      const [, month, day] = d.split("-");
      return `${month}/${day}`;
    }),
    datasets: [
      {
        // Top category is tooltip/legend only (line hidden, values preserved)
        label: topCategory?.name || `Category ${topCategoryId}`,
        data: sortedDates.map((date) => topDateRankMap[date] ?? null),
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245, 158, 11, 0.3)",
        yAxisID: "yTop",
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: true,
      },
      {
        label: bottomCategory?.name || `Category ${bottomCategoryId}`,
        data: sortedBottom.map((d) => d.salesRank),
        borderColor: "#8b5cf6",
        backgroundColor: "rgba(139, 92, 246, 0.3)",
        yAxisID: "y",
        tension: 0.3,
        spanGaps: true,
      },
    ],
  };

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        labels: { color: "white", font: { size: 10 } },
        position: "bottom",
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const val = context.parsed.y;
            return `${context.dataset.label}: #${
              val != null ? Number(val).toLocaleString() : "N/A"
            }`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        ticks: { color: "white", maxTicksLimit: 8 },
        grid: { color: "rgba(255, 255, 255, 0.2)" },
      },
      y: {
        display: true,
        reverse: true, // Lower rank is better
        min: yMin,
        max: yMax,
        ticks: {
          color: "white",
          callback: function (value) {
            return value.toLocaleString();
          },
        },
        grid: { color: "rgba(255, 255, 255, 0.2)" },
      },
      yTop: {
        display: false,
        reverse: true,
      },
    },
    elements: { point: { radius: 0 } },
    maintainAspectRatio: false,
  };

  return (
    <div
      style={{
        width: "450px",
        backgroundColor: "#4A4A4A",
        padding: "10px",
        borderRadius: "4px",
        color: "white",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div style={{ paddingBottom: "5px", fontWeight: 600 }}>
        📊 BSR by Category
      </div>
      {topCategory && topLatestRank ? (
        <div
          style={{ paddingBottom: "4px", fontSize: "11px", color: "#e5e7eb" }}
        >
          Top category: <strong>{topCategory.name}</strong> - #
          {topLatestRank.toLocaleString()}
        </div>
      ) : null}
      {bottomCategory && bottomLatestRank ? (
        <div
          style={{ paddingBottom: "6px", fontSize: "11px", color: "#e5e7eb" }}
        >
          Bottom category: <strong>{bottomCategory.name}</strong> - #
          {bottomLatestRank.toLocaleString()}
        </div>
      ) : null}
      <div style={{ height: "200px" }}>
        <Line options={options} data={chartData} />
      </div>
    </div>
  );
};

// BSR Display Component - shows BSR badge with hover tooltip (shows 3rd category)
const BSRDisplay = ({ bsrData, categories, marketplace }) => {
  if (!bsrData || !categories || categories.length === 0) {
    return null;
  }

  // Get last category
  const displayCategory = categories[categories.length - 1];
  const displayCategoryId = String(displayCategory?.catId);
  const categoryData = bsrData[displayCategoryId];

  if (!categoryData || categoryData.length === 0) {
    return null;
  }

  // Get latest rank from the display category
  const sortedData = [...categoryData].sort(
    (a, b) => new Date(b.time) - new Date(a.time)
  );
  const latestRank = sortedData[0]?.salesRank;

  if (!latestRank) {
    return null;
  }

  // Truncate category name if too long
  const categoryName = displayCategory?.name || "";
  const shortName =
    categoryName.length > 15
      ? categoryName.substring(0, 15) + "…"
      : categoryName;
  const amazonDomain =
    marketplace === "ca"
      ? "amazon.ca"
      : marketplace === "mx"
      ? "amazon.com.mx"
      : marketplace === "uk"
      ? "amazon.co.uk"
      : marketplace === "de"
      ? "amazon.de"
      : "amazon.com";
  const bestSellersUrl = `https://${amazonDomain}/gp/bestsellers/atomic/${displayCategoryId}`;

  return (
    <Tooltip
      title={<BSRHistoricalGraph bsrData={bsrData} categories={categories} />}
      componentsProps={{
        tooltip: {
          sx: { backgroundColor: "transparent", maxWidth: "none" },
        },
      }}
      placement="bottom"
    >
      <span
        style={{
          marginLeft: 10,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span
          style={{
            padding: "2px 6px",
            backgroundColor: "#fef3c7",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: 500,
            color: "#92400e",
          }}
        >
          <a
            href={bestSellersUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#92400e", textDecoration: "none" }}
          >
            📊 {shortName}
          </a>
        </span>
        <span
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          #{latestRank.toLocaleString()}
        </span>
      </span>
    </Tooltip>
  );
};

const AdjustedSalesGraph = ({ adjustedSalesData }) => {
  if (!adjustedSalesData || !adjustedSalesData.series?.length) {
    return null;
  }

  const series = adjustedSalesData.series;

  // Each ASIN carries its own date grid, so build the x-axis as the union of
  // every entry's dates, and look values up by date rather than array index.
  const dateSet = new Set();
  series.forEach((s) => (s.dates || []).forEach((d) => dateSet.add(d)));
  const dates = Array.from(dateSet).sort();

  const seriesWithIndex = series.map((s) => ({
    s,
    idxByDate: new Map((s.dates || []).map((d, i) => [d, i])),
  }));

  const sumSeries = (field) =>
    dates.map((date) => {
      let sum = 0;
      let hasValue = false;
      seriesWithIndex.forEach(({ s, idxByDate }) => {
        const idx = idxByDate.get(date);
        if (idx === undefined) return;
        const value = s[field]?.[idx];
        if (value !== undefined) {
          sum += value;
          hasValue = true;
        }
      });
      return hasValue ? sum : null;
    });

  const datasets = [
    {
      label: "Original Target",
      data: sumSeries("original_target"),
      borderColor: "#36A2EB",
      backgroundColor: "rgba(54, 162, 235, 0.5)",
      spanGaps: true,
    },
    {
      label: "Adjusted Target",
      data: sumSeries("adj_target"),
      borderColor: "#FF6384",
      backgroundColor: "rgba(255, 99, 132, 0.5)",
      spanGaps: true,
    },
  ];

  const chartData = {
    labels: dates,
    datasets: datasets,
  };

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          color: "white",
          font: {
            size: 11,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2);
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: "white",
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          color: "rgba(255, 255, 255, 0.2)",
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: "Units",
          color: "white",
          font: {
            size: 12,
          },
        },
        ticks: {
          color: "white",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.2)",
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        left: 10,
        right: 10,
        bottom: 10,
      },
    },
    backgroundColor: "#4A4A4A",
  };

  return (
    <div
      style={{
        width: "500px",
        backgroundColor: "#4A4A4A",
        padding: "10px",
        borderRadius: "4px",
        color: "white",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div style={{ paddingBottom: "5px" }}>
        <strong>Adjusted Sales Target</strong>
      </div>
      <div style={{ paddingBottom: "8px", fontSize: "12px" }}>
        Original target vs. current target (after daily rollover)
      </div>
      <div style={{ height: "200px" }}>
        <Line options={options} data={chartData} />
      </div>
    </div>
  );
};

function slackChannelPerBrand(store) {
  switch (store) {
    case "volko":
      return "C06ER403154";
    case "garnetics":
      return "C06NQ5TGW15";
    case "duramont":
      return "C06J5ETKZV3";
    case "buttertree":
      return "C06MHN0DRK8";
    case "trueocity":
      return "C06MAG1UGPR";
    case "atlasonix":
      return "C06J7DA9M7Y";
    case "vos":
      return "C06EH6M66CX";
    case "rushmorerose":
      return "C070EER4S8Z";
    default:
      return "C03DS77LF53";
  }
}

// Amazon's in-work + shipped FBA inbound for one product, minus the units its
// AWD report already shows moving to FBA. Amazon's number covers every
// shipment with a plan, AWD transfers included, so showing both rows counted
// one transfer twice (garnetics USFLAGZIPPER52-55, Oct 2026: "192" from AWD
// and "208" from the factory were the same units). What is left is inbound
// that did not come from AWD. Never below zero: the AWD report lags Amazon's
// receipts by about a day.
function amazonInboundBeyondAwd(product) {
  const amazonInbound = product?.on_the_way_quantity?.InWorkInShipped || 0;
  const awdOutbound = parseInt(product?.awd_report?.outbound) || 0;
  return Math.max(0, amazonInbound - awdOutbound);
}

function calculations(products, _compareConditions) {
  let quantity3pl = 0,
    quantityAWD = 0,
    quantityAWDInbound = 0,
    quantityAWDOutbound = 0,
    quantityfba = 0,
    quantityfbaAvailable = 0,
    quantityfbaReserved = 0,
    quantityfbaInReceiving = 0,
    quantityfbaInWork = 0,
    shipped_eta = [],
    shipped_qty = 0,
    inWorkInShipped_qty = 0,
    externalQuantity = {},
    quantityCustomerOrders = 0,
    quantityTransfer = 0,
    quantityProcessing = 0,
    asinInventoryInCA = { val: 0, asins: [] },
    awdInboundEta = [];

  if (products) {
    products.forEach((product) => {
      //3pl_quantity_column_variables
      if (product) {
        externalQuantity[product.asin] = 0;

        if (product["3pl"] && !isNaN(parseInt(product["3pl"].quantity))) {
          quantity3pl += parseInt(product["3pl"].quantity);
          externalQuantity[product.asin] += parseInt(product["3pl"].quantity);
        }

        if (
          product["awd_report"] &&
          !isNaN(parseInt(product["awd_report"].quantity))
        ) {
          quantityAWD += parseInt(product["awd_report"].quantity);
          quantityAWDInbound += parseInt(product["awd_report"].inbound);
          quantityAWDOutbound += parseInt(product["awd_report"].outbound);
          if (product["awd_report"].inbound_eta) {
            awdInboundEta.push(product["awd_report"].inbound_eta);
          }
          externalQuantity[product.asin] += parseInt(
            product["awd_report"].quantity
          );
          externalQuantity[product.asin] += parseInt(
            product["awd_report"].inbound
          );
          externalQuantity[product.asin] -= parseInt(
            product["awd_report"].outbound
          );
        }

        if (
          "fba_report" in product &&
          Object.keys(product.fba_report).length !== 0
        ) {
          //fba_quantity_column_variables
          quantityfba += product.fba_report?.quantity;
          quantityfbaAvailable += product.fba_report?.available;
          quantityfbaReserved += product.fba_report?.reserved;
          quantityfbaInReceiving += product.fba_report?.receiving;
          quantityfbaInWork += product.in_work_quantity || 0;

          inWorkInShipped_qty += amazonInboundBeyondAwd(product);

          shipped_qty += product.on_the_way_quantity?.qty || 0;

          if (product.on_the_way_quantity?.eta != null) {
            shipped_eta.push(product.on_the_way_quantity?.eta); // "Many"
          }
          if (product.fba_report && "customerOrders" in product.fba_report) {
            quantityCustomerOrders += product.fba_report.customerOrders;
          }
          if (product.fba_report && "processing" in product.fba_report) {
            quantityProcessing += product.fba_report.processing;
          }
          if (product.fba_report && "transfer" in product.fba_report) {
            quantityTransfer += product.fba_report.transfer;
          }
          if (
            product.fba_report &&
            "asin_inventory_in_ca" in product.fba_report &&
            !asinInventoryInCA.asins.includes(product.asin)
          ) {
            // sum only different asins (as this value in on an ASIN level)
            asinInventoryInCA.val += product.fba_report.asin_inventory_in_ca;
            asinInventoryInCA.asins.push(product.asin);
          }
        }
      }
    });
  }

  return {
    quantity3pl,
    quantityAWD,
    quantityAWDInbound,
    quantityAWDOutbound,
    quantityfba,
    quantityfbaAvailable,
    quantityfbaReserved,
    quantityfbaInReceiving,
    quantityfbaInWork,
    shipped_eta,
    shipped_qty,
    inWorkInShipped_qty,
    externalQuantity,
    quantityCustomerOrders,
    quantityTransfer,
    quantityProcessing,
    asinInventoryInCA,
    awdInboundEta,
  };
}

const formatRow = (
  row,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) => {
  Object.keys(row).forEach((key) => {
    if (key !== "total" && key !== "id") {
      row[key] = formatValue(row, key, convertCurrency, getCurrencySymbol);
    }
  });
};

const formatValue = (
  row,
  key,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) => {
  if (key === "field") {
    return row[key];
  }
  if (row.field === "profit" || row.field === "ppc" || row.field === "sales") {
    row[key].value = `${getCurrencySymbol()}${Number(
      convertCurrency(row[key].value)
    ).toLocaleString()}`;
    return row[key];
  } else if (row.field === "units") {
    row[key].value = `${Number(row[key].value).toLocaleString()}`;
    return row[key];
  } else {
    const value = row[key]
      ? Number(convertCurrency(row[key])).toLocaleString()
      : 0;
    return `${getCurrencySymbol()}${value}`;
  }
};

const getTableDailyData = (
  salesData,
  profitabilityData,
  ranksData,
  predictedUnitsData,
  externalStockData,
  leaf,
  store,
  ranksLoading = false,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) => {
  let formatedSalesData = tableSalesDataForProduct(
    leaf === "yes" ? [salesData] : Object.values(salesData),
    convertCurrency,
    getCurrencySymbol
  );

  let formatedProfitData = tableProfitDataForProduct(
    leaf === "yes" ? [profitabilityData] : Object.values(profitabilityData),
    convertCurrency,
    getCurrencySymbol
  );

  formatedSalesData.push(...formatedProfitData);

  let formatedRanksData = [];
  let weightedRankRow = {
    id: 10,
    total: { value: "⚖️ weighted rank", breakdown: {} },
    field: "rank",
  };
  if (
    leaf === "yes" ||
    leaf === true ||
    (salesData && Object.keys(salesData).length === 1)
  ) {
    // For "yes" the caller already drills into the single ASIN (passes a term
    // map directly). For every other code path that reaches the child branch
    // (leaf === true from getChildDailyData, or a single-child product on any
    // periodicity like "day"/"week"/"month") the ranksData is still keyed by
    // ASIN, so we need to drill into the first (and only) ASIN here.
    const childRanksInput =
      leaf === "yes" ? ranksData : Object.values(ranksData || {})[0];
    formatedRanksData = tableRanksDataForChildByRelevancy(childRanksInput);
    // Fill any rank slots that relevancy didn't populate (including the case
    // where none did) with the highest-SV keywords that aren't already shown.
    formatedRanksData = fillEmptyRankSlotsWithSV(
      formatedRanksData,
      childRanksInput
    );

    const salesDataForWeightedRank =
      leaf === true || (salesData && Object.keys(salesData).length === 1)
        ? Object.values(salesData)[0]
        : salesData;

    if (salesDataForWeightedRank) {
      salesDataForWeightedRank?.dates?.forEach((date, index) => {
        weightedRankRow[date] =
          salesDataForWeightedRank?.weightedRank?.[index] || 0;
      });
    }
  } else {
    // on multiple children
    const topSellingChildKey = Object.keys(salesData).reduce((a, b) => {
      const sumA = salesData[a].sales.reduce((sum, val) => sum + val, 0);
      const sumB = salesData[b].sales.reduce((sum, val) => sum + val, 0);
      return sumB > sumA ? b : a;
    });
    formatedRanksData = tableRanksForParentTop3ByChildren(
      Object.values(ranksData),
      ranksData[topSellingChildKey]
    );
    // The relevancy-based parent builder only backfills from sibling *relevancy*
    // keywords, so when there aren't enough relevancy keywords it leaves empty
    // rank rows. Fill any remaining slots with the top-SV keywords (which can be
    // non-relevancy keywords) so parent rank rows are never blank. This also
    // covers the previous "no relevancy at all" case.
    formatedRanksData = fillEmptyRankSlotsWithRows(
      formatedRanksData,
      tableRanksForParentTop3BySV(Object.values(ranksData))
    );
  }

  // While the ranks endpoint is still loading and we don't have real rank
  // data yet, render placeholder rank rows so the cells can show a loading
  // skeleton instead of being empty/absent. Real rank rows already carry both
  // a "rank" field and a populated "total", so we leave those untouched.
  // Note: for parent rows the empty-data helpers can return no rows at all, so
  // we synthesize a fixed set of placeholder rows here (ids 6-8 match the
  // child placeholders and won't collide with the other metric rows).
  const hasRealRanks = formatedRanksData.some(
    (row) => row.field === "rank" && row.total
  );
  if (ranksLoading && !hasRealRanks) {
    formatedRanksData = [6, 7, 8].map((id) => ({
      id,
      field: "rank",
      loading: true,
    }));
  }

  formatedRanksData.push(weightedRankRow);
  formatedSalesData.push(...formatedRanksData);

  //Adding POAS to the mix
  if (
    formatedSalesData.length > 4 &&
    formatedSalesData[2]?.["total"]?.value &&
    formatedSalesData[3]?.["total"]?.value &&
    formatedSalesData[4]?.["total"]?.value &&
    typeof formatedSalesData[2]["total"].value === "string"
  ) {
    let sales = parseFloat(
      formatedSalesData[2]["total"].value.replace(/[$,]/g, "")
    );
    let ppcSpend = parseFloat(
      formatedSalesData[3]["total"].value.replace(/[$,]/g, "")
    );
    let profitWithPPCMargin =
      (parseFloat(formatedSalesData[4]["total"].value.replace(/[$,]/g, "")) +
        ppcSpend) /
      sales;
    let sponseredSales =
      formatedSalesData[2]["total"].breakdown?.["Sponsored sales"];

    let sponseredProfit = sponseredSales * profitWithPPCMargin;
    let poas = sponseredProfit / ppcSpend;

    formatedSalesData[3]["total"].breakdown["POAS"] = poas;
  }

  let formatedPredictedUnitsData = tablePredictedUnitsDataForProduct(
    predictedUnitsData,
    leaf === "yes" || leaf === true ? salesData : Object.values(salesData)[0]
  );

  if (formatedPredictedUnitsData) {
    formatedSalesData.unshift(...formatedPredictedUnitsData);
  }

  return formatedSalesData;
};

const emptyProfitBreakdownObject = () => {
  return {
    "Gross Sales": 0,
    VAT: 0,
    Promotions: 0,
    "Refunds (Smoothed)": 0,
    "Refunds (actual)": 0,
    Referrals: 0,
    Fulfillments: 0,
    Cogs: 0,
    // "Returned Cogs": 0,
    "PPC Product": 0,
    "PPC Brand": 0,
    "PPC Display": 0,
    "Extra fees": 0,
  };
};

// Upcoming days (as "MM-DD", chronological) that get their own target-only
// columns: from tomorrow to VELOCITY_TOOL_EXTRA_DAYS after today, for any
// window that starts in that span or is running, cut at the window's last
// day. A window starting in 3 days still gets all the days (the ones before
// it starts have no target), and one ending in 2 days gets 2. Only when the
// table ends today: after a past range, the next days aren't upcoming.
const getFutureTargetDates = (
  predictedUnitsByAsin,
  salesDates,
  today = moment()
) => {
  if (
    !salesDates ||
    salesDates.length === 0 ||
    salesDates[salesDates.length - 1] !== today.format("MM-DD")
  ) {
    return [];
  }
  const todayDate = today.format("YYYY-MM-DD");
  const upcomingDays = Array.from(
    { length: VELOCITY_TOOL_EXTRA_DAYS },
    (_, i) =>
      today
        .clone()
        .add(i + 1, "days")
        .format("YYYY-MM-DD")
  );
  const lastUpcomingDay = upcomingDays[upcomingDays.length - 1];
  const futureDates = new Set();

  Object.values(predictedUnitsByAsin || {}).forEach((entry) => {
    if (!entry || !Array.isArray(entry.dates) || entry.dates.length === 0) {
      return;
    }
    const sorted = [...entry.dates].sort();
    const windowStart = sorted[0];
    const windowEnd = sorted[sorted.length - 1];
    if (windowStart > lastUpcomingDay || windowEnd <= todayDate) return;
    upcomingDays
      .filter((d) => d <= windowEnd)
      .forEach((d) => futureDates.add(d));
  });

  return Array.from(futureDates)
    .sort()
    .map((d) => d.slice(-5));
};

const tablePredictedUnitsDataForProduct = (predictedUnitsByAsin, salesData) => {
  if (!salesData || !salesData.dates || salesData.dates.length === 0) {
    return null;
  }

  // Each ASIN now carries its own independent { dates, original_target,
  // adj_target } grid (no shared date axis across ASINs), so every lookup
  // below is done by matching real date strings rather than array index.
  const entries = Object.values(predictedUnitsByAsin || {}).filter(
    (entry) =>
      entry &&
      Array.isArray(entry.dates) &&
      entry.dates.length > 0 &&
      (entry.adj_target || entry.original_target)
  );

  if (entries.length === 0) {
    return null;
  }

  // salesData.dates (the table's own date columns) are short "MM-DD"
  // strings, while each ASIN's own `dates` here are full "YYYY-MM-DD" —
  // index by the MM-DD suffix so the two line up.
  const entriesWithIndex = entries.map((entry) => ({
    entry,
    idxByDate: new Map(entry.dates.map((d, i) => [d.slice(-5), i])),
  }));

  const dateRange = salesData.dates;

  let predictedUnitsRow = {
    id: 9,
    // Upcoming days have their own columns, so the total cell only hosts
    // the original vs. adjusted target graph in its tooltip.
    total: { value: "📈 targets", breakdown: {} },
    field: "target",
  };

  // Fill the table's own dates plus the upcoming days returned past them,
  // which get their own target-only columns.
  const futureDates = getFutureTargetDates(predictedUnitsByAsin, dateRange);

  [...dateRange, ...futureDates].forEach((date) => {
    const cell = {
      value: 0,
      breakdown: { "Original target": 0 },
    };
    let inAnyWindow = false;

    entriesWithIndex.forEach(({ entry, idxByDate }) => {
      const idx = idxByDate.get(date);
      if (idx === undefined) return;
      inAnyWindow = true;

      cell.value += entry.adj_target?.[idx] ?? 0;
      cell.breakdown["Original target"] += entry.original_target?.[idx] ?? 0;
    });

    // Upcoming days before the window starts stay empty rather than 0.
    if (inAnyWindow || !futureDates.includes(date)) {
      predictedUnitsRow[date] = cell;
    }
  });

  // Adjusted-sales data for the tooltip graph (original vs. adjusted target
  // over time). Each ASIN keeps its own dates array; the graph unions them.
  predictedUnitsRow.total.breakdown.adjustedSalesData = {
    series: entries.map((entry) => ({
      dates: entry.dates,
      original_target: entry.original_target,
      adj_target: entry.adj_target,
    })),
  };

  return [predictedUnitsRow];
};

const tableProfitDataForProduct = (
  productsData,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) => {
  const TOTAL_WINDOW_CELLS = 30;
  const recentDateKeys = new Set(
    [
      ...new Set(
        Object.values(productsData || {})
          .filter(Boolean)
          .flatMap((data) => Object.keys(data || {}))
      ),
    ]
      .sort((a, b) => new Date(a) - new Date(b))
      .slice(-TOTAL_WINDOW_CELLS)
  );

  let profitRow = {
    id: 4,
    total: { value: 0, breakdown: emptyProfitBreakdownObject() },
    field: "profit",
  };
  let marginRow = { id: 5, total: 0, field: "margin" };

  Object.values(productsData).forEach((data) => {
    if (data) {
      Object.entries(data).forEach(([date, value]) => {
        const includeInTotal = recentDateKeys.has(date);
        if (!profitRow[date]) {
          profitRow[date] = {
            value: 0,
            breakdown: emptyProfitBreakdownObject(),
          };
          marginRow[date] = 0;
        }
        // Calculate refund cost: recovery_rate * sales * refund_rate
        const refundRate = value.refund_rate || 0;
        const refundRecoveryRate = value.refund_recovery_rate ?? 1;
        const refundCost =
          refundRecoveryRate * value.orderedProductSales * refundRate;
        // Calculate profit after refund
        const profitBeforeRefund =
          value.orderedProductSales -
          value.item_promotion_discount -
          value.referral_fee -
          value.fulfillment_fee -
          value.cogs_cost -
          value.product_cost -
          value.brand_cost -
          value.display_cost +
          value.extra_fee;
        profitRow[date].value += profitBeforeRefund - refundCost;
        // value.returned_cogs;
        // Returned Cogs is not included in the profit breakdown
        profitRow[date].value = parseFloat(profitRow[date].value.toFixed(2));

        // calculate profit breakdown for the date
        // Gross Sales = orderedProductSales (net-of-VAT on the backend) + the
        // VAT stripped from it, reconstructed here so the tooltip walks down
        // from a real gross figure: Gross Sales - VAT - Promotions - ... = Profit.
        const vatAmount = value.vat_amount || 0;
        profitRow[date].breakdown["Gross Sales"] +=
          value.orderedProductSales + vatAmount;
        if (includeInTotal) {
          profitRow.total.breakdown["Gross Sales"] +=
            value.orderedProductSales + vatAmount;
        }
        profitRow[date].breakdown["VAT"] += vatAmount;
        if (includeInTotal) {
          profitRow.total.breakdown["VAT"] += vatAmount;
        }
        profitRow[date].breakdown["Promotions"] +=
          value.item_promotion_discount;
        if (includeInTotal) {
          profitRow.total.breakdown["Promotions"] +=
            value.item_promotion_discount;
        }
        profitRow[date].breakdown["Refunds (Smoothed)"] += refundCost;
        if (includeInTotal) {
          profitRow.total.breakdown["Refunds (Smoothed)"] += refundCost;
        }
        profitRow[date].breakdown["Refunds (actual)"] += value.refunded_amount;
        if (includeInTotal) {
          profitRow.total.breakdown["Refunds (actual)"] +=
            value.refunded_amount;
        }
        profitRow[date].breakdown["Referrals"] += value.referral_fee;
        if (includeInTotal) {
          profitRow.total.breakdown["Referrals"] += value.referral_fee;
        }
        profitRow[date].breakdown["Fulfillments"] += value.fulfillment_fee;
        if (includeInTotal) {
          profitRow.total.breakdown["Fulfillments"] += value.fulfillment_fee;
        }
        profitRow[date].breakdown["Cogs"] += value.cogs_cost;
        if (includeInTotal) {
          profitRow.total.breakdown["Cogs"] += value.cogs_cost;
        }
        profitRow[date].breakdown["PPC Product"] += value.product_cost;
        if (includeInTotal) {
          profitRow.total.breakdown["PPC Product"] += value.product_cost;
        }
        profitRow[date].breakdown["PPC Brand"] += value.brand_cost;
        if (includeInTotal) {
          profitRow.total.breakdown["PPC Brand"] += value.brand_cost;
        }
        profitRow[date].breakdown["PPC Display"] += value.display_cost;
        if (includeInTotal) {
          profitRow.total.breakdown["PPC Display"] += value.display_cost;
        }
        profitRow[date].breakdown["Extra fees"] += value.extra_fee;
        if (includeInTotal) {
          profitRow.total.breakdown["Extra fees"] += value.extra_fee;
        }
        // profitRow[date].breakdown["Returned Cogs"] += value.returned_cogs;
        // Returned Cogs is not included in the profit breakdown
        marginRow[date] += value.orderedProductSales;
      });
    }
  });

  Object.keys(marginRow).forEach((key) => {
    if (typeof marginRow[key] === "number" && key !== "id" && key !== "total") {
      if (recentDateKeys.has(key)) {
        profitRow.total.value += profitRow[key].value;
        marginRow.total += marginRow[key];
      }
      if (marginRow[key] === 0 || profitRow[key].value === 0) {
        marginRow[key] = "0%";
      } else {
        marginRow[key] =
          ((profitRow[key].value / marginRow[key]) * 100).toFixed(2) + "%";
      }
    }
  });

  marginRow.total = `${(
    (profitRow.total.value / marginRow.total) *
    100
  ).toFixed(2)}%`;
  profitRow.total.value = `${getCurrencySymbol()}${Number(
    convertCurrency(parseFloat(profitRow.total.value.toFixed(2)))
  ).toLocaleString()}`;
  formatRow(profitRow, convertCurrency, getCurrencySymbol);
  return [profitRow, marginRow];
};

const formatBreakdownTooltip = (
  breakdown,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) => {
  if (breakdown.historical) {
    const term = Object.keys(breakdown).find((key) => key.includes("SV(1W)"));
    return (
      <HistoricalSVGraph
        historicalData={breakdown.historical}
        term={term ? term.replace(" SV(1W)", "") : ""}
      />
    );
  }
  if (breakdown.adjustedSalesData) {
    return (
      <AdjustedSalesGraph adjustedSalesData={breakdown.adjustedSalesData} />
    );
  }
  return (
    <div>
      {Object.entries(breakdown).map(([key, value]) => {
        let prefix = `-${getCurrencySymbol()}`;
        let isMoney = true;
        if (key === "Gross Sales") {
          // key === "Returned Cogs" is not included in the profit breakdown
          prefix = `+${getCurrencySymbol()}`;
        } else if (key.includes("sales")) {
          let precentage =
            (value /
              (breakdown["Organic sales"] + breakdown["Sponsored sales"])) *
            100;
          key = key.replace("sales", "(" + precentage.toFixed(0) + "%)");
          prefix = getCurrencySymbol();
        } else if (key.includes("units")) {
          let precentage =
            (value /
              (breakdown["Organic units"] + breakdown["Sponsored units"])) *
            100;
          key = key.replace("units", "(" + precentage.toFixed(0) + "%)");
          prefix = "";
          isMoney = false;
        } else if (key === "TACOS" || key === "ACOS") {
          prefix = "%";
          isMoney = false;
        } else if (
          key === "POAS" ||
          key.includes("SV(1W)") ||
          key === "target" ||
          key === "Original target"
        ) {
          prefix = "";
          isMoney = false;
        } else if (key === "Extra fees") {
          if (value > 0) {
            prefix = `+${getCurrencySymbol()}`;
          } else {
            prefix = `-${getCurrencySymbol()}`;
            value = Math.abs(value);
          }
        }
        const displayValue = isMoney ? Number(convertCurrency(value)) : value;
        const formattedValue =
          displayValue % 1 === 0 ? displayValue : displayValue.toFixed(2);

        // Style "Refunds (actual)" differently
        const isActualRefund = key === "Refunds (actual)";
        return (
          <div
            key={key}
            style={
              isActualRefund
                ? {
                    fontStyle: "italic",
                    color: "lightgrey",
                    fontSize: "0.8em",
                  }
                : {}
            }
          >
            {key}: {prefix}
            {Number(formattedValue).toLocaleString()}
          </div>
        );
      })}
    </div>
  );
};

// Conversion rate (CVR) of a sales cell, as the Advanced Report defines it:
// the backend's per-ASIN-per-day `cvr` (order items / sessions, in %). A
// single child-day shows that value as is; a parent row or the 30-day total
// combines several the way the Advanced Report does for a parent ASIN —
// order items summed over sessions summed, i.e. the sessions-weighted mean
// of the parts (calculateRow keeps Σ cvr×sessions and Σ sessions on the
// cell). null when the cell has no sessions: the S&T report lags the
// trailing days, and the FinanceReport gap-fill and the empty placeholder
// carry no traffic — shown as n/a rather than a misleading 0%.
const cellConversionRate = (cell) => {
  const sessions = Number(cell?.sessions) || 0;
  if (sessions <= 0) return null;
  return (Number(cell.cvrWeighted) || 0) / sessions;
};

const formatCvrLine = (cell) => {
  const cvr = cellConversionRate(cell);
  return <div>CVR: {cvr === null ? "n/a" : `${cvr.toFixed(2)}%`}</div>;
};

// Sales-cell tooltip: the CVR line, under the organic/sponsored breakdown on
// parent rows (child rows had no sales tooltip before, so they get CVR only).
const formatSalesCellTooltip = (
  cell,
  includeBreakdown,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) => {
  const cvrLine = formatCvrLine(cell);
  if (!includeBreakdown || !cell.breakdown) return cvrLine;
  return (
    <div>
      {formatBreakdownTooltip(
        cell.breakdown,
        convertCurrency,
        getCurrencySymbol
      )}
      {cvrLine}
    </div>
  );
};

// Date header with a small tag above it ("TODAY", "UPCOMING").
const TaggedDateHeader = ({ tag, date, color }) => (
  <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
    <span
      style={{
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        color,
      }}
    >
      {tag}
    </span>
    <span>{date}</span>
  </span>
);

TaggedDateHeader.propTypes = {
  tag: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  color: PropTypes.string,
};

// The weighted-rank row only carries data when a single ASIN is on screen
// (see getTableDailyData): the drilled-in child, or a product with one child.
// Resolve that ASIN's seasonality entry (from /weekly-seasonality-index, keyed
// by child ASIN) so the row's cells can show the week's index on hover.
const resolveWeightedRankSeasonality = (
  seasonalityData,
  salesData,
  leaf,
  product
) => {
  if (!seasonalityData) return null;
  let asin = null;
  if (leaf === "yes") {
    asin = product?.child_asin;
  } else if (salesData && Object.keys(salesData).length === 1) {
    asin = Object.keys(salesData)[0];
  }
  return asin ? seasonalityData[asin] || null : null;
};

const tableColumnsForProduct = (
  data,
  leaf,
  asin,
  store,
  comments,
  marketplace,
  start_date = null,
  end_date = null,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$",
  futureDates = [],
  seasonality = null,
  // Adds the pinned spacer column the "Next N days" handle sits on.
  withUpcomingHandle = false
) => {
  // Function to determine cell color based on value
  const domainExtension =
    marketplace === "ca"
      ? ".ca"
      : marketplace === "mx"
      ? ".com.mx"
      : marketplace === "uk"
      ? ".co.uk"
      : marketplace === "de"
      ? ".de"
      : ".com";

  // make this a true gradient where the 1 is the start of the gradient and the 30 is the end of the gradient
  // using the css gradient function
  // background: linear-gradient(to right, rgba(76, 175, 80, 0.5) 0%, rgba(76, 175, 80, 0.2) 50%, rgba(255, 235, 59, 0.2) 75%, rgba(255, 152, 0, 0.2) 100%);
  // return the css gradient function
  // const getCellColorClass = (position) => {
  //   return `linear-gradient(to right, rgba(76, 175, 80, 0.5) ${position * 10}%, rgba(76, 175, 80, 0.2) ${position * 10}%, rgba(255, 235, 59, 0.2) ${position * 10}%, rgba(255, 152, 0, 0.2) ${position * 10}%)`;
  // };

  const getCellColorClass = (position) => {
    if (position >= 1 && position < 6) return "strong-green-cell"; // "rgba(76, 175, 80, 0.5)";
    if (position >= 6 && position < 11) return "light-green-cell"; // rgba(76, 175, 80, 0.2)";
    if (position >= 11 && position < 21) return "yellow-cell"; // rgba(255, 235, 59, 0.2)";
    if (position >= 21 && position < 30) return "orange-cell"; // "rgba(255, 152, 0, 0.2)";
    if (position >= 30) return "red-cell"; //"rgba(244, 67, 54, 0.2)";
    return "transparent";
  };

  // Date columns are "MM-DD".
  const today = moment().format("MM-DD");
  // Today's units are still coming in, so they're marked as partial.
  const renderCellValue = (rowField, date, value) =>
    rowField === "units" && date === today ? (
      <span>
        {value}{" "}
        <span style={{ fontSize: "10px", color: "#94a3b8" }}>so far</span>
      </span>
    ) : (
      <span>{value}</span>
    );

  let columns = [];
  if (data) {
    const reversedDates = [...data.dates].reverse();
    columns = reversedDates.map((date) => {
      const dateIndex = data.dates.indexOf(date);
      const deal = data.deals[dateIndex];
      const headerClassName = deal && deal !== "" ? "deal-column" : "";

      return {
        field: date,
        headerName: date,
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        headerClassName: () => headerClassName,
        cellClassName: (params) => {
          if (params.row.field === "rank") {
            // For weighted rank row (id === 10), use a subtle background for sparkline visibility
            if (params.row.id === 10) {
              return "weighted-rank-cell";
            }
            const rowData = params.row[date];
            // Handle both simple numeric ranks and complex object ranks
            if (typeof rowData === "object" && rowData !== null) {
              // For complex ranking data, use the organic rank for color
              const organicRank = rowData.organic || 0;
              return getCellColorClass(organicRank);
            } else {
              // For simple numeric ranking data
              return getCellColorClass(rowData);
            }
          } else if (params.row.field === "target") {
            return "target-cell";
          }
          return "";
        },
        renderCell: (params) => {
          const rowField = params.row.field;
          const rowData = params.row[date];

          if (rowField === "sales" && rowData) {
            return (
              <Tooltip
                title={formatSalesCellTooltip(
                  rowData,
                  leaf !== "yes",
                  convertCurrency,
                  getCurrencySymbol
                )}
              >
                <span>{rowData.value}</span>
              </Tooltip>
            );
          } else if (rowField === "units" && leaf === "yes") {
            return renderCellValue(rowField, date, rowData.value);
          } else if (
            (rowField === "profit" ||
              rowField === "ppc" ||
              rowField === "units" ||
              rowField === "target") &&
            rowData
          ) {
            const tooltipContent = formatBreakdownTooltip(
              rowData.breakdown,
              convertCurrency,
              getCurrencySymbol
            );
            return (
              <Tooltip title={tooltipContent}>
                {renderCellValue(rowField, date, rowData.value)}
              </Tooltip>
            );
          } else if (
            rowData &&
            rowData.breakdown &&
            !rowData.breakdown.historical
          ) {
            const tooltipContent = formatBreakdownTooltip(
              rowData.breakdown,
              convertCurrency,
              getCurrencySymbol
            );
            return (
              <Tooltip
                title={tooltipContent}
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "transparent",
                    },
                  },
                }}
              >
                <span>{rowData.value}</span>
              </Tooltip>
            );
          } else if (rowField === "rank") {
            if (params.row.loading) {
              return (
                <Skeleton
                  variant="text"
                  width="60%"
                  sx={{ mx: "auto" }}
                  animation="wave"
                />
              );
            }
            // Handle ranking data with potential sponsored ranking
            if (
              typeof rowData === "object" &&
              rowData !== null &&
              (rowData.organic !== undefined || rowData.sponsored !== undefined)
            ) {
              const organicRank = rowData.organic || 0;
              const sponsoredRank = rowData.sponsored;

              return (
                <div
                  style={{
                    padding: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {sponsoredRank && (
                    <>
                      <div
                        style={{
                          display: "inline-block",
                          height: "0",
                          width: "0",
                          borderTop: "35px solid rgb(233 213 255)",
                          borderRight: "35px solid transparent",
                          position: "absolute",
                          top: "0",
                          left: "0",
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          height: "18px",
                          width: "18px",
                          textAlign: "center",
                          fontSize: "10px",
                          color: "#000",
                        }}
                      >
                        {sponsoredRank}
                      </span>
                    </>
                  )}
                  {organicRank === 0 ? "-" : organicRank}
                </div>
              );
            } else {
              // Fallback for simple ranking data (including weighted rank row)
              const isWeightedRankRow = params.row.id === 10;

              // Get sparkline values for weighted rank row
              let prevValue = null;
              let nextValue = null;
              let allValues = [];

              if (isWeightedRankRow && reversedDates) {
                const dateIndex = reversedDates.indexOf(date);

                // Get prev and next values for smooth curve
                if (dateIndex > 0) {
                  prevValue = params.row[reversedDates[dateIndex - 1]] || 0;
                }
                if (dateIndex < reversedDates.length - 1) {
                  nextValue = params.row[reversedDates[dateIndex + 1]] || 0;
                }

                // Get all values for proper min/max scaling
                allValues = reversedDates.map((d) => params.row[d] || 0);
              }

              return (
                <div
                  style={{
                    padding: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {isWeightedRankRow && allValues.length > 1 && (
                    <WeightedRankSparkline
                      prevValue={prevValue}
                      currentValue={rowData}
                      nextValue={nextValue}
                      allValues={allValues}
                    />
                  )}
                  <span
                    style={{
                      position: "relative",
                      zIndex: 1,
                      fontWeight: isWeightedRankRow ? 600 : 400,
                      fontSize: isWeightedRankRow ? "13px" : "inherit",
                      color: isWeightedRankRow ? "#1e293b" : "inherit",
                      textShadow: isWeightedRankRow
                        ? "0 1px 2px rgba(255,255,255,0.8)"
                        : "none",
                    }}
                  >
                    {rowData === 0
                      ? "-"
                      : typeof rowData === "number"
                      ? rowData.toFixed(2)
                      : rowData}
                  </span>
                </div>
              );
            }
          }
        },
        renderHeader: (params) => (
          <CommentableHeader
            {...params}
            label={
              date === today ? (
                <TaggedDateHeader tag="TODAY" date={date} color="#1d4ed8" />
              ) : undefined
            }
            asin={asin}
            store={store}
            dbComments={comments}
            start_date={start_date}
            end_date={end_date}
            marketplace={marketplace}
          />
        ),
      };
    });
  }

  // Upcoming days only have a target, so every other row stays blank. Kept
  // newest-first like the date columns above.
  const futureColumns = [...futureDates].reverse().map((date) => ({
    field: date,
    headerName: date,
    width: 100,
    sortable: false,
    disableColumnMenu: true,
    headerClassName: "future-target-column",
    cellClassName: (params) =>
      params.row.field === "target"
        ? "target-cell future-target-column"
        : "future-target-column",
    renderCell: (params) => {
      const rowData = params.row[date];
      if (params.row.field !== "target" || !rowData) {
        return null;
      }
      return (
        <Tooltip
          title={formatBreakdownTooltip(
            rowData.breakdown,
            convertCurrency,
            getCurrencySymbol
          )}
        >
          <span>{rowData.value}</span>
        </Tooltip>
      );
    },
    renderHeader: () => (
      <Tooltip title="Upcoming day: target only">
        <span>
          <TaggedDateHeader tag="UPCOMING" date={date} color="#6b7280" />
        </span>
      </Tooltip>
    ),
  }));
  columns.unshift(...futureColumns);

  if (withUpcomingHandle) {
    columns.unshift({
      field: UPCOMING_HANDLE_FIELD,
      headerName: "",
      width: UPCOMING_TAB_WIDTH,
      minWidth: UPCOMING_TAB_WIDTH,
      resizable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => null,
      renderCell: () => null,
    });
  }

  columns.unshift({
    field: "total",
    headerName: "Total/Avg (30d)",
    width: 100,
    renderHeader: () => (
      <Tooltip title="Sums or averages the data over the last 30 days">
        <span>
          Total/Avg{" "}
          <span style={{ fontSize: "0.78em", color: "#6b7280" }}>(30d)</span>
        </span>
      </Tooltip>
    ),
    renderCell: (params) => {
      const rowField = params.row.field;
      const totalData = params.row.total;

      if (rowField === "sales" && totalData) {
        return (
          <Tooltip
            title={formatSalesCellTooltip(
              totalData,
              leaf !== "yes",
              convertCurrency,
              getCurrencySymbol
            )}
          >
            <span
              style={
                leaf === "yes"
                  ? undefined
                  : { whiteSpace: "normal", color: "black" }
              }
            >
              {totalData.value}
            </span>
          </Tooltip>
        );
      } else if (rowField === "units" && leaf === "yes") {
        return <span>{totalData.value}</span>;
      } else if (
        rowField === "profit" ||
        rowField === "units" ||
        rowField === "target" ||
        rowField === "ppc"
      ) {
        const tooltipContent = formatBreakdownTooltip(
          totalData.breakdown,
          convertCurrency,
          getCurrencySymbol
        );
        return (
          <Tooltip title={tooltipContent}>
            <span
              style={{
                whiteSpace: rowField === "target" ? "pre-line" : "normal",
                fontSize:
                  rowField === "rank"
                    ? "11px"
                    : rowField === "target"
                    ? "10px"
                    : "inherit",
                lineHeight: rowField === "target" ? 1.2 : "inherit",
                fontStyle: rowField === "target" ? "italic" : "normal",
                color: rowField === "target" ? "gray" : "black",
              }}
            >
              {totalData.value}
            </span>
          </Tooltip>
        );
      } else if (rowField === "rank") {
        if (params.row.loading) {
          return <Skeleton variant="text" width="70%" animation="wave" />;
        }
        if (params.row.id === 10) {
          // The weighted-rank row label: hovering it shows the ASIN's weekly
          // seasonality index over the whole published cycle (no Amazon
          // search link here -- the label isn't a keyword).
          const label = (
            <span
              style={{
                whiteSpace: "normal",
                fontSize: "11px",
                fontWeight: 300,
                color: "black",
                cursor: seasonality ? "help" : "default",
              }}
            >
              {totalData.value}
            </span>
          );
          return seasonality ? (
            <Tooltip
              title={
                <WeightedRankSeasonalityTooltip seasonality={seasonality} />
              }
              placement="right"
              componentsProps={{ tooltip: { sx: { maxWidth: "none", p: 0 } } }}
            >
              {label}
            </Tooltip>
          ) : (
            label
          );
        }
        const tooltipContent = formatBreakdownTooltip(
          totalData.breakdown,
          convertCurrency,
          getCurrencySymbol
        );
        return (
          //change domain of link by marketplace
          <Tooltip title={tooltipContent}>
            <a
              href={`https://www.amazon${domainExtension}/s?k=${totalData.value}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
              }}
            >
              <span
                style={{
                  whiteSpace: "normal",
                  fontSize: "11px",
                  fontWeight: 300,
                  color: "black",
                }}
              >
                {totalData.value}
              </span>
            </a>
          </Tooltip>
        );
      }
    },
  });

  columns.unshift({
    field: "field",
    headerName: "",
    width: 100,
  });

  return columns;
};

const calculateRow = (rows, data, field, index, productsData) => {
  const TOTAL_WINDOW_CELLS = 30;
  const totalStartIndex = Math.max(0, data.dates.length - TOTAL_WINDOW_CELLS);
  const initBreakdown = {
    value: 0,
    breakdown: { [`Organic ${field}`]: 0, [`Sponsored ${field}`]: 0 },
  };
  const initPPC = {
    value: 0,
    breakdown: { TACOS: 0, ACOS: 0 },
  };
  let defaultRow = {
    id: index,
    total: field === "units" || field === "sales" ? initBreakdown : 0,
    field,
  };

  if (field === "ppc") {
    defaultRow.total = initPPC;
  }

  let row = rows[index] || defaultRow;

  data.dates.forEach((date, i) => {
    const includeInTotal = i >= totalStartIndex;
    let value;
    let breakdown = {};

    // Determine value and breakdown based on the field
    if (field === "ppc") {
      value = data[field][i];
      breakdown = {
        "PPC Brand": data["ppc_b"][i],
        "PPC Display": data["ppc_d"][i],
        "PPC Product": data["ppc_p"][i],
      };
    } else if (field === "units") {
      value = data[field][i];
      // Attribution windows (click date, 14d lookback) can still put a day's
      // sponsored above its total; organic never goes below zero.
      breakdown = {
        "Organic units": Math.max(0, value - data.sponsoredUnits[i]),
        "Sponsored units": data.sponsoredUnits[i],
      };
    } else if (field === "sales") {
      value = data[field][i];
      breakdown = {
        "Organic sales": Math.max(0, value - data.sponsoredSales[i]),
        "Sponsored sales": data.sponsoredSales[i],
      };
    } else {
      value =
        field === "avg_price"
          ? calculateAvgPrice(data, i, productsData)
          : data[field][i];
    }

    // Update row for the current date
    if (field === "ppc" || field === "units" || field === "sales") {
      if (row[date]) {
        row[date].value += value;
        Object.entries(breakdown).forEach(([key, val]) => {
          row[date].breakdown[key] += val;
        });
      } else {
        row[date] = { value, breakdown: { ...breakdown } };
      }
      if (row[date].value) {
        row[date].value = Number(row[date].value.toFixed(2));
      }

      if (field === "sales") {
        // Conversion-rate inputs for the sales-cell tooltip (see
        // cellConversionRate), kept beside the formatted value: the
        // backend's per-day CVR weighted by its sessions, so a parent row
        // (children) or the 30-day total (days) combines them the way the
        // Advanced Report does for a parent ASIN — order items summed over
        // sessions summed. Both are 0 on days the S&T report hasn't covered.
        const cellSessions = Number(data.sessions?.[i]) || 0;
        const cellCvr = Number(data.cvr?.[i]) || 0;
        row[date].sessions = (row[date].sessions || 0) + cellSessions;
        row[date].cvrWeighted =
          (row[date].cvrWeighted || 0) + cellCvr * cellSessions;
        if (includeInTotal) {
          row.total.sessions = (row.total.sessions || 0) + cellSessions;
          row.total.cvrWeighted =
            (row.total.cvrWeighted || 0) + cellCvr * cellSessions;
        }
      }

      if (field === "units" || field === "sales") {
        if (includeInTotal) {
          row.total.value += value;
          Object.entries(breakdown).forEach(([key, val]) => {
            row.total.breakdown[key] += val;
          });
        }
      } else if (field === "ppc") {
        if (includeInTotal) {
          row.total.value += value;
        }
      } else {
        if (includeInTotal) {
          row.total += value;
        }
      }
    } else {
      row[date] = row[date] ? row[date] + value : value;
      if (includeInTotal) {
        row.total += value;
      }
    }
  });

  return row;
};

const calculateAvgPrice = (data, i, productsData) => {
  let allSales = 0;
  let allUnits = 0;
  productsData = productsData.filter((product) => product["dates"].length > 0);
  productsData.forEach((product) => {
    allSales += product["sales"][i];
    allUnits += product["units"][i];
  });
  let price = allSales / (allUnits === 0 ? 1 : allUnits) / productsData.length;
  price = Number(price.toFixed(2));
  return Object.values(productsData).length > 1 ? price : data["avg_price"][i];
};

const tableRanksForParentTop3BySV = (productsData) => {
  let rows = [];
  let allChildrenTop3 = [];

  //gather terms from all children, top 3 from each child
  productsData.forEach((product) => {
    let top3 = [];
    if (product && Object.keys(product).length > 0) {
      for (const [key, value] of Object.entries(product)) {
        top3.push({ key, value });
        top3.sort((a, b) => b.value.sv_1w - a.value.sv_1w);
        if (top3.length > 3) {
          top3.pop();
        }
      }
    }
    if (top3.length > 0) {
      allChildrenTop3.push(...top3);
    }
  });
  //sort unique terms by sv_1w and take overall top 3
  allChildrenTop3.sort((a, b) => b.value.sv_1w - a.value.sv_1w);
  const uniqueTermsSorted = new Set(allChildrenTop3.map((item) => item.key));
  const top3Terms = Array.from(uniqueTermsSorted).slice(0, 3);

  //for each term in the overall top 3 terms,
  //find the minimum rank for each date in one of the children
  //and merge sponsored/organic rankings from different children
  top3Terms.forEach((keyToMatch, index) => {
    const matchingChildValues = allChildrenTop3.filter(
      (item) => item.key === keyToMatch
    );
    let minRankOnTerm = {
      key: keyToMatch,
      value: {
        sv_1w: matchingChildValues[0].value.sv_1w,
        relevancy: matchingChildValues[0].value.relevancy || 0,
        historical: matchingChildValues[0].value.historical,
      },
    };
    matchingChildValues.forEach((childValue) => {
      Object.entries(childValue.value).forEach(([keyDate, value]) => {
        if (
          keyDate === "sv_1w" ||
          keyDate === "relevancy" ||
          keyDate === "historical"
        )
          return;

        if (!minRankOnTerm.value[keyDate]) {
          minRankOnTerm.value[keyDate] = {
            organic: 0,
            sponsored: null,
          };
        }

        // If value is an object with organic/sponsored, handle it properly
        if (typeof value === "object" && value !== null) {
          const organicRank = value.organic || 0;
          const sponsoredRank = value.sponsored || null;

          // Take the best organic rank
          if (
            organicRank !== 0 &&
            typeof minRankOnTerm.value[keyDate] === "object" &&
            minRankOnTerm.value[keyDate] !== null &&
            (minRankOnTerm.value[keyDate].organic === 0 ||
              organicRank < minRankOnTerm.value[keyDate].organic)
          ) {
            minRankOnTerm.value[keyDate].organic = organicRank;
          }

          // Take the best sponsored rank
          if (
            sponsoredRank &&
            typeof minRankOnTerm.value[keyDate] === "object" &&
            minRankOnTerm.value[keyDate] !== null &&
            (!minRankOnTerm.value[keyDate].sponsored ||
              sponsoredRank < minRankOnTerm.value[keyDate].sponsored)
          ) {
            minRankOnTerm.value[keyDate].sponsored = sponsoredRank;
          }

          // Take the best historical rank
          if (
            value.historical &&
            typeof minRankOnTerm.value[keyDate] === "object" &&
            minRankOnTerm.value[keyDate] !== null &&
            (minRankOnTerm.value[keyDate].historical === null ||
              value.historical < minRankOnTerm.value[keyDate].historical)
          ) {
            minRankOnTerm.value[keyDate].historical = value.historical;
          }
        } else {
          // Handle simple numeric ranking
          if (
            value !== 0 &&
            typeof minRankOnTerm.value[keyDate] === "object" &&
            minRankOnTerm.value[keyDate] !== null &&
            (minRankOnTerm.value[keyDate].organic === 0 ||
              value < minRankOnTerm.value[keyDate].organic)
          ) {
            minRankOnTerm.value[keyDate].organic = value;
          }
        }

        // Don't convert to simple format here - do it after processing all children
      });
    });

    // After processing all children, convert to simple format if only organic exists
    Object.keys(minRankOnTerm.value).forEach((keyDate) => {
      if (
        keyDate !== "sv_1w" &&
        keyDate !== "relevancy" &&
        keyDate !== "historical"
      ) {
        const dateValue = minRankOnTerm.value[keyDate];
        if (
          typeof dateValue === "object" &&
          dateValue !== null &&
          dateValue.organic &&
          !dateValue.sponsored &&
          !dateValue.historical
        ) {
          minRankOnTerm.value[keyDate] = dateValue.organic;
        }
      }
    });

    let toolKey = minRankOnTerm.key + " SV(1W)";
    let row = {
      id: index + 6,
      total: {
        value: minRankOnTerm.key,
        breakdown: {
          [toolKey]: minRankOnTerm.value.sv_1w,
          historical: minRankOnTerm.value.historical,
        },
      },
      field: "rank",
      ...minRankOnTerm.value,
    };
    rows.push(row);
  });
  return rows;
};

const tableRanksForParentTop3ByChildren = (productsData, topSellingChild) => {
  const rowsInTable = 5;
  let rows = [
    { id: rowsInTable + 1 },
    { id: rowsInTable + 2 },
    { id: rowsInTable + 3 },
  ];

  // Use ONLY the top-selling child's relevancy keywords. Borrowing relevancy
  // keywords from sibling size-variants (e.g. the 8x12 child contributing
  // "8x12 american flag") pulled tiny, size-specific terms into the parent.
  // Any slots the top-selling child doesn't fill are backfilled with high-SV
  // keywords later by fillEmptyRankSlotsWithRows.
  const relevancyTermsMap = new Map(); // term -> { sv_1w, relevancy }
  if (topSellingChild) {
    Object.entries(topSellingChild).forEach(([term, value]) => {
      if (Number(value.relevancy) > 0) {
        relevancyTermsMap.set(term, {
          sv_1w: value.sv_1w || 0,
          relevancy: Number(value.relevancy),
        });
      }
    });
  }

  // Order the top-selling child's relevancy keywords by relevancy (1, 2, 3) and
  // pack them into the top slots so the most relevant one shows first.
  let threeTerms = Array.from(relevancyTermsMap.entries())
    .sort((a, b) => a[1].relevancy - b[1].relevancy)
    .slice(0, 3)
    .map(([term]) => term);

  threeTerms.forEach((term, termIndex) => {
    //search for the term in all children and find the minimum rank on each date
    //and merge sponsored/organic rankings from different children
    let minRankOnTerm = {
      key: term,
      value: { sv_1w: 0, relevancy: 0, historical: null },
    };
    for (let i = 0; i < productsData.length; i++) {
      const childValue = productsData[i][term];
      if (childValue) {
        Object.entries(childValue).forEach(([keyDate, value]) => {
          if (
            keyDate === "sv_1w" ||
            keyDate === "relevancy" ||
            keyDate === "historical"
          ) {
            if (keyDate === "sv_1w") {
              minRankOnTerm.value.sv_1w = childValue.sv_1w;
            } else if (keyDate === "relevancy") {
              minRankOnTerm.value.relevancy = childValue.relevancy;
            } else if (keyDate === "historical") {
              minRankOnTerm.value.historical = childValue.historical;
            }
            return;
          }

          if (!minRankOnTerm.value[keyDate]) {
            minRankOnTerm.value[keyDate] = {
              organic: 0,
              sponsored: null,
            };
          }

          // If value is an object with organic/sponsored, handle it properly
          if (typeof value === "object" && value !== null) {
            const organicRank = value.organic || 0;
            const sponsoredRank = value.sponsored || null;

            // Take the best organic rank
            if (
              organicRank !== 0 &&
              typeof minRankOnTerm.value[keyDate] === "object" &&
              minRankOnTerm.value[keyDate] !== null &&
              (minRankOnTerm.value[keyDate].organic === 0 ||
                organicRank < minRankOnTerm.value[keyDate].organic)
            ) {
              minRankOnTerm.value[keyDate].organic = organicRank;
            }

            // Take the best sponsored rank
            if (
              sponsoredRank &&
              typeof minRankOnTerm.value[keyDate] === "object" &&
              minRankOnTerm.value[keyDate] !== null &&
              (!minRankOnTerm.value[keyDate].sponsored ||
                sponsoredRank < minRankOnTerm.value[keyDate].sponsored)
            ) {
              minRankOnTerm.value[keyDate].sponsored = sponsoredRank;
            }

            // Take the best historical rank
            if (
              value.historical &&
              typeof minRankOnTerm.value[keyDate] === "object" &&
              minRankOnTerm.value[keyDate] !== null &&
              (minRankOnTerm.value[keyDate].historical === null ||
                value.historical < minRankOnTerm.value[keyDate].historical)
            ) {
              minRankOnTerm.value[keyDate].historical = value.historical;
            }
          } else {
            // Handle simple numeric ranking
            if (
              value !== 0 &&
              typeof minRankOnTerm.value[keyDate] === "object" &&
              minRankOnTerm.value[keyDate] !== null &&
              (minRankOnTerm.value[keyDate].organic === 0 ||
                value < minRankOnTerm.value[keyDate].organic)
            ) {
              minRankOnTerm.value[keyDate].organic = value;
            }
          }

          // Don't convert to simple format here - do it after processing all children
        });
      }
    }

    // After processing all children, convert to simple format if only organic exists
    Object.keys(minRankOnTerm.value).forEach((keyDate) => {
      if (
        keyDate !== "sv_1w" &&
        keyDate !== "relevancy" &&
        keyDate !== "historical"
      ) {
        const dateValue = minRankOnTerm.value[keyDate];
        if (
          typeof dateValue === "object" &&
          dateValue !== null &&
          dateValue.organic &&
          !dateValue.sponsored &&
          !dateValue.historical
        ) {
          minRankOnTerm.value[keyDate] = dateValue.organic;
        }
      }
    });

    let toolKey = minRankOnTerm.key + " SV(1W)";
    let row = {
      id: termIndex + 1 + rowsInTable,
      total: {
        value: minRankOnTerm.key,
        breakdown: {
          [toolKey]: minRankOnTerm.value.sv_1w,
          historical: minRankOnTerm.value.historical,
        },
      },
      field: "rank",
      ...minRankOnTerm.value,
    };
    // Use termIndex to ensure all 3 slots are filled sequentially
    rows[termIndex] = row;
  });

  return rows;
};

const tableRanksDataForChildByRelevancy = (productData) => {
  const rowsInTable = 5;
  let rows = [
    { id: rowsInTable + 1 },
    { id: rowsInTable + 2 },
    { id: rowsInTable + 3 },
  ];

  if (!productData) return rows;

  // Keep only keywords that have a real relevancy (1/2/3) and order them by
  // relevancy ascending. We then pack them into the top slots so relevancy
  // keywords always render above any SV-backfilled keywords. Using the raw
  // relevancy value as the slot index (the old behavior) left a gap above a
  // keyword whose relevancy was, say, 2 with no relevancy-1 keyword, and that
  // gap got filled by SV — pushing an SV keyword above the relevancy one.
  const relevantEntries = Object.entries(productData)
    .filter(([, value]) => Number(value.relevancy) > 0)
    .sort((a, b) => Number(a[1].relevancy) - Number(b[1].relevancy));

  relevantEntries.forEach(([key, value], index) => {
    if (index >= rows.length) return;
    const toolKey = key + " SV(1W)";
    rows[index] = {
      id: rowsInTable + index + 1,
      total: {
        value: key,
        breakdown: {
          [toolKey]: value.sv_1w,
          historical: value.historical,
        },
      },
      field: "rank",
      ...value,
    };
  });

  return rows;
};

// Fills empty rank slots (placeholders that only carry an `id`) with the
// highest-SV keywords that aren't already displayed. This complements the
// relevancy-based fill so that when only some relevancy slots are populated,
// the remaining lines are backfilled from SV instead of rendering empty.
const fillEmptyRankSlotsWithSV = (rows, productData) => {
  if (!productData) return rows;
  const rowsInTable = 5;

  const usedKeys = new Set(
    rows
      .filter((row) => row && row.total && row.total.value)
      .map((row) => row.total.value)
  );

  const candidates = Object.entries(productData)
    .filter(([key, value]) => value.sv_1w != null && !usedKeys.has(key))
    .sort((a, b) => (b[1].sv_1w || 0) - (a[1].sv_1w || 0));

  let candidateIndex = 0;
  for (let i = 0; i < rows.length; i++) {
    const isEmpty = !rows[i] || Object.keys(rows[i]).length === 1;
    if (!isEmpty) continue;
    if (candidateIndex >= candidates.length) break;

    const [key, value] = candidates[candidateIndex++];
    const toolKey = key + " SV(1W)";
    rows[i] = {
      id: rowsInTable + i + 1,
      total: {
        value: key,
        breakdown: {
          [toolKey]: value.sv_1w,
          historical: value.historical,
        },
      },
      field: "rank",
      ...value,
    };
  }

  return rows;
};

// Fills empty rank slots with already-built rows (used by the parent path,
// where each candidate row is a merged-across-children rank row). Skips
// candidates whose keyword is already displayed and re-stamps the slot id.
const fillEmptyRankSlotsWithRows = (rows, candidateRows) => {
  const rowsInTable = 5;

  const usedKeys = new Set(
    rows
      .filter((row) => row && row.total && row.total.value)
      .map((row) => row.total.value)
  );

  const candidates = (candidateRows || []).filter(
    (row) =>
      row && row.total && row.total.value && !usedKeys.has(row.total.value)
  );

  let candidateIndex = 0;
  for (let i = 0; i < rows.length; i++) {
    const isEmpty = !rows[i] || Object.keys(rows[i]).length === 1;
    if (!isEmpty) continue;
    if (candidateIndex >= candidates.length) break;

    rows[i] = { ...candidates[candidateIndex++], id: rowsInTable + i + 1 };
  }

  return rows;
};

const tableSalesDataForProduct = (
  productsData,
  convertCurrency = (v) => v,
  getCurrencySymbol = () => "$"
) => {
  let rows = [];
  const rowsToExclude = new Set([
    "dates",
    "deals",
    "ppc_b",
    "ppc_d",
    "ppc_p",
    "sponsoredUnits",
    "sponsoredSales",
    "long_term_storage_data",
    "weightedRank",
    "weightedRankBreakdown",
    // Sales-cell CVR tooltip inputs (calculateRow), not rows.
    "sessions",
    "cvr",
  ]);
  Object.values(productsData).forEach((data) => {
    if (data && data.dates && data.dates.length > 0) {
      rows = Object.keys(data)
        .filter((x) => !rowsToExclude.has(x))
        .map((field, index) =>
          calculateRow(rows, data, field, index, productsData)
        );
    }
  });
  rows.forEach((row) => formatRow(row, convertCurrency, getCurrencySymbol));
  if (
    rows.length > 0 &&
    typeof rows[0] === "object" &&
    typeof rows[2] === "object" &&
    typeof rows[3] === "object" &&
    "total" in rows[2] &&
    "total" in rows[3] &&
    rows[0].total !== null &&
    rows[2].total !== null &&
    rows[3].total !== null
  ) {
    rows[3].total.breakdown["TACOS"] =
      (rows[3].total.value / rows[2].total.value) * 100;
    rows[3].total.breakdown["ACOS"] =
      (rows[3].total.value / rows[2].total.breakdown["Sponsored sales"]) * 100;
    rows[1].total = `${getCurrencySymbol()}${Number(
      convertCurrency((rows[2].total.value / rows[0].total.value).toFixed(2))
    ).toLocaleString()}`;
    rows[3].total.value = `${getCurrencySymbol()}${Number(
      convertCurrency(rows[3].total.value)
    ).toLocaleString()}`;
    rows[2].total.value = `${getCurrencySymbol()}${Number(
      convertCurrency(rows[2].total.value)
    ).toLocaleString()}`;
    rows[0].total.value = Number(rows[0].total.value).toLocaleString();
  }
  return rows;
};

const calcPpcPerUnit = (data) => {
  let ppcPerUnit = 0;
  if (data) {
    if ("units" in data) {
      //child
      ppcPerUnit = 0;
      if ("ppc" in data) {
        const last7Ppc = data.ppc.slice(-7);
        const last7Units = data.units.slice(-7);
        ppcPerUnit =
          last7Ppc.reduce((sumPpc, currPpc) => sumPpc + currPpc, 0) /
          last7Units.reduce((sumUnits, currUnits) => sumUnits + currUnits, 0);
      }
    } else {
      //parent
      Object.values(data).forEach((value) => {
        ppcPerUnit += calcPpcPerUnit(value);
      });
      ppcPerUnit = ppcPerUnit / Object.keys(data).length;
    }
  }
  ppcPerUnit =
    isNaN(Number(ppcPerUnit.toFixed(2))) || ppcPerUnit === Infinity
      ? 0
      : Number(ppcPerUnit.toFixed(2));
  return ppcPerUnit;
};

// Virtualisation window of each row's grid (DataTable props below). The grid
// renders the cells in its scroll window plus a buffer on each side, and once
// the window has moved by the threshold it re-renders all its cells
// synchronously (MUI flushSync) inside the scroll event. Scroll sync makes a
// parent and all its children cross together, so with the defaults (3 rows /
// 3 columns) a 30-variation parent re-rendered 31 grids every 300px. Rows are
// few: render them all once and never re-render vertically. Columns (~60
// dates by default) keep virtualising, with the burst every 1000px instead.
// `disableColumnVirtualization`, which these replace, was a v5 prop that
// stopped existing with MUI X v6 and had been a no-op since.
const GRID_ROW_BUFFER = 20;
const GRID_COLUMN_BUFFER = 10;

// Scroll sync across a "family" of grids: a parent row's grid and the grids of
// all its children. The grid the user scrolls writes every other member
// directly (see ProductRowDetailed), so there is no relay and nothing is ever
// written back to the grid the user is holding.
const createScrollFamily = () => {
  const members = new Set();
  // Where the family was last scrolled to, so a grid that mounts later (a
  // child row opened, or a row scrolled back into view) can catch up.
  let lastPosition = null;
  return {
    register(member) {
      members.add(member);
      return () => members.delete(member);
    },
    broadcast(source, params) {
      lastPosition = { left: params.left, top: params.top };
      members.forEach((member) => {
        if (member !== source) member.follow(params);
      });
    },
    get lastPosition() {
      return lastPosition;
    },
  };
};
const isNear = (a, b) => Math.abs(a - b) < 1;

const DataTable = styled(DataGridPro)({
  ".MuiDataGrid-main>div:nth-child(3)": {
    display: "none",
  },
  "& .MuiDataGrid-pinnedColumns": {
    backgroundColor: "white",
  },
  "& .MuiDataGrid-columnsContainer": {
    backgroundColor: "#fafafa",
  },
  "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
    borderRight: "1px solid #f0f0f0",
  },
  "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
    borderBottom: "1px solid #f0f0f0",
  },
  "& .MuiDataGrid-cell": {
    color: "rgba(0,0,0,.85)",
    userSelect: "none",
  },
  "& .MuiDataGrid-cellContent": {
    overflow: "visible",
  },
  "& .MuiPaginationItem-root": {
    borderRadius: 0,
  },
  "& .MuiDataGrid-footerContainer": {
    display: "none",
  },
  "& .strong-green-cell": {
    backgroundColor: "rgba(76, 175, 80, 0.5)",
  },
  "& .light-green-cell": {
    backgroundColor: "rgba(76, 175, 80, 0.2)",
  },
  "& .yellow-cell": {
    backgroundColor: "rgba(255, 235, 59, 0.2)",
  },
  "& .orange-cell": {
    backgroundColor: "rgba(255, 152, 0, 0.2)",
  },
  "& .red-cell": {
    backgroundColor: "rgba(244, 67, 54, 0.2)",
  },
  "& .deal-column": {
    backgroundColor: "rgba(121, 123, 234, 0.25)",
  },
  "& .target-cell": {
    color: "gray",
    fontStyle: "italic",
  },
  "& .future-target-column": {
    backgroundColor: "rgba(148, 163, 184, 0.08)",
  },
  "& .weighted-rank-cell": {
    backgroundColor: "rgba(248, 250, 252, 0.9)",
    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
  },
  "& .rank-row .MuiDataGrid-cell:not([data-field='field'])": {
    padding: "0 !important",
  },
});

// Add this new component after the imports and before the RuleEngineDecisionWithIcon component
const CustomSuggestionIcon = ({ color = "#1976d2", size = 22 }) => (
  <>
    <style>
      {`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.35); }
          100% { transform: scale(1); }
        }
      `}
    </style>
    <img
      src="/assets/custom-suggestion-icon-transparent.png"
      alt="Suggestion"
      style={{
        width: size,
        height: size,
        opacity: color === "#666666" ? 0.4 : 1,
        filter:
          color === "#666666" ? "grayscale(100%) brightness(0.6)" : "none",
        animation: "pulse 2s ease-in-out infinite",
      }}
    />
  </>
);

const RossAnalysisIcon = ({ size = 22 }) => (
  <>
    <style>
      {`
        @keyframes rossIconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.35); }
        }
        @keyframes rossBadgePulse {
          0%, 100% { transform: scale(1); opacity: 0.95; }
          50% { transform: scale(1.08); opacity: 1; }
        }
      `}
    </style>
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src="/assets/custom-suggestion-icon-transparent.png"
        alt="Ross analysis"
        style={{
          width: size,
          height: size,
          animation: "rossIconPulse 2s ease-in-out infinite",
          position: "relative",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: -7,
          bottom: -6,
          width: Math.max(14, Math.floor(size * 0.62)),
          height: Math.max(14, Math.floor(size * 0.62)),
          borderRadius: "50%",
          backgroundColor: "transparent",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "rossBadgePulse 1.8s ease-in-out infinite",
          boxShadow: "none",
          zIndex: 3,
          pointerEvents: "none",
        }}
      >
        <SearchIcon
          sx={{
            fontSize: Math.max(12, Math.floor(size * 0.45)),
            color: "#a855f7",
          }}
        />
      </Box>
    </Box>
  </>
);

// `ppcDecision`: an Iris Reduce PPC decision, shown with its own card instead
// of the price one
const RuleEngineDecisionWithIcon = ({ ppcDecision, ...props }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { saveRuleEngineResponseStatus } = useSelector(storeAnalyticsSelector);

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Hide icon if decision was completed and closed
  const agentName = (
    props.agentName || (props.action?.includes("Profit -") ? "pete" : "iris")
  ).toLowerCase();
  const statusKey = `${props.executionId}_${props.asin}_${agentName}`;
  const status = saveRuleEngineResponseStatus[statusKey];
  if (status?.complete && status?.closed) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 10,
      }}
    >
      <IconButton
        size="small"
        onClick={handleIconClick}
        sx={{
          backgroundColor: "transparent",
          color: "#1976d2",
          "&:hover": {
            backgroundColor: "rgba(25, 118, 210, 0.04)",
          },
          width: 28,
          height: 28,
          "@keyframes pulse": {
            "0%": {
              transform: "scale(1)",
            },
            "50%": {
              transform: "scale(1.35)",
            },
            "100%": {
              transform: "scale(1)",
            },
          },
        }}
      >
        <CustomSuggestionIcon size={22} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionProps={{
          onEntered: (node) => fitPopoverToViewport(node),
        }}
        sx={{
          "& .MuiPopover-paper": {
            maxWidth: 450,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2,
          },
        }}
      >
        <div style={{ padding: 16 }}>
          {ppcDecision ? (
            <Box sx={{ px: 2, pb: 1, overflow: "hidden" }}>
              <IrisPpcCard
                decision={ppcDecision}
                asin={props.asin}
                sku={props.sku}
                store={props.store}
                marketplace={props.marketplace}
                onRemove={handleClose}
                compact
              />
            </Box>
          ) : (
            <RuleEngineDecision {...props} />
          )}
        </div>
      </Popover>
    </div>
  );
};

const RossDecisionWithIcon = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { saveRuleEngineResponseStatus } = useSelector(storeAnalyticsSelector);

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Hide icon if decision was completed and closed
  const statusKey = `${props.executionId}_${props.asin}_ross`;
  const status = saveRuleEngineResponseStatus[statusKey];
  if (status?.complete && status?.closed) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 10,
      }}
    >
      <IconButton
        size="small"
        onClick={handleIconClick}
        sx={{
          backgroundColor: "transparent",
          color: "#1976d2",
          "&:hover": {
            backgroundColor: "rgba(25, 118, 210, 0.04)",
          },
          width: 28,
          height: 28,
        }}
      >
        <RossAnalysisIcon size={22} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionProps={{
          onEntered: (node) => fitPopoverToViewport(node),
        }}
        sx={{
          "& .MuiPopover-paper": {
            maxWidth: 420,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2,
          },
        }}
      >
        <div style={{ padding: 16 }}>
          <RossDecisionCard {...props} />
        </div>
      </Popover>
    </div>
  );
};

const SamDecisionWithIcon = ({ asin, store, marketplace, samDecisions }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [removed, setRemoved] = useState(false);

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (removed) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 10,
      }}
    >
      <IconButton
        size="small"
        onClick={handleIconClick}
        sx={{
          backgroundColor: "transparent",
          color: "#4caf50",
          "&:hover": {
            backgroundColor: "rgba(76, 175, 80, 0.04)",
          },
          width: 28,
          height: 28,
          "@keyframes pulse": {
            "0%": {
              transform: "scale(1)",
            },
            "50%": {
              transform: "scale(1.35)",
            },
            "100%": {
              transform: "scale(1)",
            },
          },
        }}
      >
        <CustomSuggestionIcon color="#4caf50" size={22} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionProps={{
          onEntered: (node) => fitPopoverToViewport(node),
        }}
        sx={{
          "& .MuiPopover-paper": {
            maxWidth: 420,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2,
          },
        }}
      >
        <div style={{ padding: 16 }}>
          {/* Agent header — replaces the outer wrapper present in AgentRecommendations */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Avatar
              src="/assets/sam.png"
              sx={{
                width: 28,
                height: 28,
                border: "2px solid #4caf50",
                backgroundColor: "#fff",
              }}
            />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#4caf50", lineHeight: 1 }}
              >
                Sam
              </Typography>
              <Typography variant="caption" sx={{ color: "#999" }}>
                Found a high-opportunity keyword
              </Typography>
            </Box>
          </Box>
          <SamDecisionCard
            asin={asin}
            store={store}
            marketplace={marketplace}
            samDecisions={samDecisions}
            onRemove={() => {
              handleClose();
              setRemoved(true);
            }}
          />
        </div>
      </Popover>
    </div>
  );
};

const ReginaDecisionWithIcon = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { saveRuleEngineResponseStatus } = useSelector(storeAnalyticsSelector);

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Hide icon if decision was completed and closed
  const statusKey = `${props.executionId}_${props.asin}_regina`;
  const status = saveRuleEngineResponseStatus[statusKey];
  if (status?.complete && status?.closed) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 10,
      }}
    >
      <IconButton
        size="small"
        onClick={handleIconClick}
        sx={{
          backgroundColor: "transparent",
          color: "#c62828",
          "&:hover": {
            backgroundColor: "rgba(198, 40, 40, 0.04)",
          },
          width: 28,
          height: 28,
          "@keyframes pulse": {
            "0%": {
              transform: "scale(1)",
            },
            "50%": {
              transform: "scale(1.35)",
            },
            "100%": {
              transform: "scale(1)",
            },
          },
        }}
      >
        <CustomSuggestionIcon color="#c62828" size={22} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionProps={{
          onEntered: (node) => fitPopoverToViewport(node),
        }}
        sx={{
          "& .MuiPopover-paper": {
            maxWidth: 420,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2,
          },
        }}
      >
        <div style={{ padding: 16 }}>
          <ReginaDecisionCard {...props} />
        </div>
      </Popover>
    </div>
  );
};

// Constrains a Popover paper to fit within the viewport once MUI finishes positioning
const fitPopoverToViewport = (paperNode) => {
  const rect = paperNode.getBoundingClientRect();
  const available = window.innerHeight - rect.top - 16;
  paperNode.style.maxHeight = `${Math.max(available, 200)}px`;
  paperNode.style.overflow = "auto";
};

// Groups Sam decisions by keyword so multiple actions for the same keyword
// appear as a single card. Non-Sam decisions are kept as individual items.
// Regina's own decisions stay hidden here (Store Analysis never showed her
// standalone ranking alerts), but once Dave attaches a Best Deal to one,
// it's an actionable item like any other and should show through. Ross's
// velocity analyses stay excluded, but his automatic price reverts show
// next to the Iris price changes they undo ("Done for you" / manual action).
// Fiona's cards show only while they wait for a decision (or a due
// follow-up); accepted and finished ones stay in the Agents tab.
const isHiddenInStoreAnalysis = (d) => {
  if (isFionaDecision(d)) return getFionaTab(d) !== "review";
  const agentName = d?.decision?.agent?.toLowerCase() || "";
  const isRegina = agentName === "regina" || d?.version?.includes("regina");
  if (isRegina && decisionHasActiveDeal(d)) return false;
  if (isRossRevertDecision(d)) return false;
  return agentName === "ross" || isRegina || d?.version?.includes("ross");
};

// Computes the decisions that will actually be rendered as an icon for an asin.
// Mirrors the filtering done in MultiDecisionWithIcon (Ross/Regina/Fiona decisions and
// completed+closed decisions are excluded) so that the "Has recommendation"
// filter stays in sync with whether a recommendation icon is actually shown.
const getVisibleDecisionsForAsin = (
  decisions,
  asin,
  saveRuleEngineResponseStatus = {}
) => {
  if (!decisions) return [];
  if (!Array.isArray(decisions)) {
    return [decisions].filter((d) => !isHiddenInStoreAnalysis(d));
  }
  return decisions.filter((d) => {
    if (isHiddenInStoreAnalysis(d)) return false;
    const agentName = d.decision?.agent?.toLowerCase() || "unknown";
    const dStatusKey = `${d.execution_id}_${asin}_${agentName}`;
    const status = saveRuleEngineResponseStatus[dStatusKey];
    if (status?.complete && status?.closed) return false;
    // TODO: in_progress cards are disabled by now, if not used in a future just delete it
    if (agentName === "iris" && d.automated_execution_status === "in_progress")
      return false;
    // Iris is still pausing the ads
    if (getIrisPpcState(d) === "pending") return false;
    return true;
  });
};

const groupSamDecisionsForRow = (items) => {
  const result = [];
  const samGroups = {};
  items.forEach((d) => {
    const agentName = d.decision?.agent?.toLowerCase();
    const isSam = d.decision_type === "sam" || agentName === "sam";
    if (isSam) {
      const keyword = d.decision?.keyword || "";
      if (!samGroups[keyword]) {
        const group = {
          _isSamGroup: true,
          keyword,
          created_at: d.created_at,
          decisions: [],
        };
        samGroups[keyword] = group;
        result.push(group);
      }
      samGroups[keyword].decisions.push(d);
    } else {
      result.push(d);
    }
  });
  return result;
};

// Multi-Decision Wrapper - Shows accordion when multiple decisions, single card otherwise
const MultiDecisionWithIcon = ({
  decisions,
  asin,
  sku,
  store,
  marketplace,
  currentPrice,
  ...otherProps
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [removedDecisions, setRemovedDecisions] = useState(new Set());
  // Fiona cards answered here, by execution_id and card group (a run's today
  // and next-fee-schedule cards share an execution_id); "product" once a
  // reject hid all of the product's cards
  const [answeredFiona, setAnsweredFiona] = useState(new Set());
  const { saveRuleEngineResponseStatus } = useSelector(storeAnalyticsSelector);

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveDecision = (executionIdOrIds) => {
    if (Array.isArray(executionIdOrIds)) {
      setRemovedDecisions((prev) => new Set([...prev, ...executionIdOrIds]));
    } else {
      setRemovedDecisions((prev) => new Set([...prev, executionIdOrIds]));
    }
  };

  const fionaKey = (d) => `${d.execution_id}|${d.decision?.card_group || ""}`;
  // Any answer moves the card out of "to review", so it leaves this popover
  const handleFionaCardChanged = (decision, change) => {
    setAnsweredFiona((prev) =>
      new Set(prev).add(
        change?.remove === "product" ? "product" : fionaKey(decision)
      )
    );
  };

  const open = Boolean(anchorEl);

  // Filter out removed decisions (local) on top of the shared visibility logic
  // (Ross/Regina/Fiona + completed+closed decisions) used for the "Has recommendation" filter.
  const visibleDecisions = getVisibleDecisionsForAsin(
    decisions,
    asin,
    saveRuleEngineResponseStatus
  ).filter(
    (d) =>
      !removedDecisions.has(d.execution_id) &&
      !(
        isFionaDecision(d) &&
        (answeredFiona.has("product") || answeredFiona.has(fionaKey(d)))
      )
  );

  // Group same-keyword Sam decisions into a single item
  const groupedItems = groupSamDecisionsForRow(visibleDecisions);

  // If no visible decisions remaining, don't render anything
  if (groupedItems.length === 0) {
    return null;
  }

  // A single "Done for you" decision (succeeded Iris or Ross revert) skips the
  // single-card fast path so it renders via MultiDecisionAccordion and appears
  // in the "Done for you" section. Failed Ross reverts go there too, since
  // RossDecisionWithIcon only knows the velocity-analysis card, and so does a
  // Fiona card: only the accordion knows how to show and answer it.
  const isSingleAccordionOnly =
    groupedItems.length === 1 &&
    !groupedItems[0]._isSamGroup &&
    (isDoneForYouDecision(groupedItems[0]) ||
      isRossRevertDecision(groupedItems[0]) ||
      isFionaDecision(groupedItems[0]));

  // If single item (or all Sam decisions collapsed into one group), use single-card display
  if (groupedItems.length === 1 && !isSingleAccordionOnly) {
    const item = groupedItems[0];

    if (item._isSamGroup) {
      return (
        <SamDecisionWithIcon
          asin={asin}
          store={store}
          marketplace={marketplace}
          samDecisions={item.decisions}
        />
      );
    }

    const decisionData = item;
    const agentName = decisionData?.decision?.agent?.toLowerCase();
    const isRossAgent =
      agentName === "ross" || decisionData?.version?.includes("ross");
    const isSamAgent =
      decisionData?.decision_type === "sam" || agentName === "sam";
    const isReginaAgent =
      agentName === "regina" || decisionData?.version?.includes("regina");

    if (isSamAgent) {
      return (
        <SamDecisionWithIcon
          asin={asin}
          store={store}
          marketplace={marketplace}
          samDecisions={[decisionData]}
        />
      );
    }

    if (isReginaAgent) {
      return (
        <ReginaDecisionWithIcon
          asin={asin}
          sku={sku}
          store={store}
          marketplace={marketplace}
          executionId={decisionData.execution_id}
          reginaData={decisionData}
          {...otherProps}
        />
      );
    }

    if (isRossAgent) {
      return (
        <RossDecisionWithIcon
          asin={asin}
          sku={sku}
          store={store}
          marketplace={marketplace}
          executionId={decisionData.execution_id}
          rossData={decisionData}
          {...otherProps}
        />
      );
    }

    return (
      <RuleEngineDecisionWithIcon
        asin={asin}
        sku={sku}
        store={store}
        marketplace={marketplace}
        executionId={decisionData.execution_id}
        targetPrice={decisionData.decision?.target_price || null}
        sellingPrice={decisionData.selling_price || null}
        action={decisionData.decision?.action}
        agentName={decisionData.decision?.agent}
        currentPrice={currentPrice}
        decisionType={decisionData.decision_type || "price"}
        dealAvailable={decisionData.deal_available}
        deal={decisionData.deal}
        automatedExecutionStatus={decisionData.automated_execution_status}
        ppcDecision={isIrisPpcDecision(decisionData) ? decisionData : null}
        {...otherProps}
      />
    );
  }

  // Multiple items - show accordion
  const decisionCount = groupedItems.length;

  return (
    <div
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        zIndex: 10,
      }}
    >
      <Badge
        badgeContent={decisionCount > 1 ? decisionCount : null}
        color="secondary"
        sx={{
          "& .MuiBadge-badge": {
            backgroundColor: "#9c27b0",
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.65rem",
          },
        }}
      >
        <IconButton
          size="small"
          onClick={handleIconClick}
          sx={{
            backgroundColor: "transparent",
            color: "#9c27b0", // Purple color to indicate multiple decisions
            "&:hover": {
              backgroundColor: "rgba(156, 39, 176, 0.04)",
            },
            width: 28,
            height: 28,
            "@keyframes pulse": {
              "0%": {
                transform: "scale(1)",
              },
              "50%": {
                transform: "scale(1.35)",
              },
              "100%": {
                transform: "scale(1)",
              },
            },
          }}
        >
          <CustomSuggestionIcon color="#9c27b0" size={22} />
        </IconButton>
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionProps={{
          onEntered: (node) => fitPopoverToViewport(node),
        }}
        sx={{
          "& .MuiPopover-paper": {
            maxWidth: 500,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2,
          },
        }}
      >
        <MultiDecisionAccordion
          decisions={groupedItems}
          asin={asin}
          sku={sku}
          store={store}
          marketplace={marketplace}
          currentPrice={currentPrice}
          onRemoveDecision={handleRemoveDecision}
          onFionaCardChanged={handleFionaCardChanged}
          {...otherProps}
        />
      </Popover>
    </div>
  );
};

// The 50px product picture. Asks Amazon's CDN for a small rendition instead
// of decoding the full-size catalog image, and falls back to the original URL
// should the CDN refuse the modifier. Mounted only while its row is near the
// viewport (see ProductRowDetailed).
const ProductThumbnail = ({ src, alt }) => {
  const thumbnail = productThumbnailUrl(src);
  const [useOriginal, setUseOriginal] = useState(false);
  return (
    <img
      src={useOriginal ? src : thumbnail}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (!useOriginal && thumbnail !== src) setUseOriginal(true);
      }}
      style={{
        width: 50,
        height: 50,
        borderRadius: 5,
      }}
      data-testid="product_image"
    />
  );
};

ProductThumbnail.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
};

const ProductRowDetailed = ({
  store,
  sku,
  product,
  compareConditions = {},
  leaf,
  openAllRows,
  openAllInventory,
  inventoryData,
  salesData,
  unitEconomicsData,
  profitabilityData,
  ranksData,
  longTermStorageData,
  deliveryTimesData,
  predictedUnitsData,
  ruleEngineDecisionsData,
  asinFeaturesData,
  comments,
  filters,
  setParentShow,
  scrollFamily,
  upcomingFamily,
  marketplace,
  bsrData,
  seasonalityData = {},
}) => {
  const charMax = 45;

  const apiRef = useGridApiRef();
  const { user } = useAuth();
  const { convertCurrency, getCurrencySymbol } = useCurrency();
  const { productRanksLoading, saveRuleEngineResponseStatus } = useSelector(
    storeAnalyticsSelector
  );
  const domainExtension =
    marketplace === "ca"
      ? ".ca"
      : marketplace === "mx"
      ? ".com.mx"
      : marketplace === "uk"
      ? ".co.uk"
      : marketplace === "de"
      ? ".de"
      : ".com";

  ProductRowDetailed.propTypes = {
    sku: PropTypes.string,
    store: PropTypes.string,
    product: PropTypes.object,
    compareConditions: PropTypes.object,
    leaf: PropTypes.string,
    openAllRows: PropTypes.bool,
    openAllInventory: PropTypes.bool,
    inventoryData: PropTypes.object,
    salesData: PropTypes.object,
    unitEconomicsData: PropTypes.object,
    profitabilityData: PropTypes.object,
    ranksData: PropTypes.object,
    longTermStorageData: PropTypes.object,
    predictedUnitsData: PropTypes.object,
    ruleEngineDecisionsData: PropTypes.object,
    filters: PropTypes.array,
    setParentShow: PropTypes.func,
    scrollFamily: PropTypes.object,
    // From the parent row: { dates, shown, setShown } for its upcoming days.
    upcomingFamily: PropTypes.object,
    marketplace: PropTypes.string,
    bsrData: PropTypes.object,
    seasonalityData: PropTypes.object,
  };

  const [openModal, setOpenModal] = useState(false); // State to control modal visibility
  const [inventoryCellHeight, setInventoryCellHeight] = useState(260); // Dynamic height based on inventory cell
  const inventoryCellRef = useRef(null);

  const handleCellClick = (params) => {
    const { row } = params;
    if (row.field === "rank") {
      trackEvent("rank_tracking_modal_opened", {
        asin: product.children ? product.parent_asin : product.child_asin,
        store,
      });
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false); // Close the modal
  };

  const columnsSalesData =
    leaf === "yes"
      ? salesData
      : Object.values(salesData || {}).filter((x) => x?.dates?.length > 0)[0];

  // Upcoming days (target-only columns between the pinned columns and TODAY).
  // Hidden until someone opens them, so the table starts at TODAY. A parent
  // row owns the state for its whole family, and its children use the
  // parent's dates (the union of theirs), so every grid in the family has the
  // same columns and their shared scrolling stays aligned.
  const ownUpcomingDates = getFutureTargetDates(
    predictedUnitsData,
    columnsSalesData?.dates
  );
  const [ownUpcomingShown, setOwnUpcomingShown] = useState(false);
  const upcomingDates = upcomingFamily
    ? upcomingFamily.dates
    : ownUpcomingDates;
  const upcomingShown = upcomingFamily
    ? upcomingFamily.shown
    : ownUpcomingShown;
  const setUpcomingShown = upcomingFamily
    ? upcomingFamily.setShown
    : setOwnUpcomingShown;
  const hasUpcoming = upcomingDates.length > 0;
  const upcomingTargetUnits = sumUpcomingTargets(
    predictedUnitsData,
    upcomingDates
  );
  const [upcomingSeen, setUpcomingSeen] = useState(hasSeenUpcomingDays);
  useEffect(() => onUpcomingDaysSeen(() => setUpcomingSeen(true)), []);

  const toggleUpcomingDays = () => {
    const next = !upcomingShown;
    setUpcomingShown(next);
    if (next) {
      markUpcomingDaysSeen();
      setUpcomingSeen(true);
    }
    trackEvent("velocity_upcoming_days_toggled", {
      shown: next,
      days: upcomingDates.length,
      store,
    });
  };

  // Observe inventory cell height changes
  useEffect(() => {
    const cell = inventoryCellRef.current;
    if (!cell) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        // Use measured height with a minimum of 260px
        setInventoryCellHeight(Math.max(260, height));
      }
    });

    resizeObserver.observe(cell);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const {
    quantity3pl,
    quantityAWD,
    quantityAWDInbound,
    quantityAWDOutbound,
    quantityfbaAvailable,
    quantityfbaReserved,
    quantityfbaInReceiving,
    quantityfbaInWork,
    shipped_qty,
    inWorkInShipped_qty,
    shipped_eta,
    externalQuantity,
    quantityCustomerOrders,
    quantityTransfer,
    quantityProcessing,
    asinInventoryInCA,
    awdInboundEta,
  } = calculations(inventoryData, compareConditions);

  const showWeightedRankChange = (data) => {
    const weightedRank = calcWeightedRank(data);
    const weightedRankChange = calcWeightedRankChange(data);
    const changeStr =
      weightedRankChange === Infinity || isNaN(weightedRankChange)
        ? 0
        : Math.abs(weightedRankChange).toFixed(0) + "%";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          // marginLeft: "10px",
        }}
      >
        <div style={{ color: "black" }}>{weightedRank.toFixed(2)}</div>
        <div style={{ color: "black", fontSize: 12, marginLeft: 2 }}>
          <div
            style={{
              color: weightedRankChange < 0 ? "green" : "red",
              display: "inline-block",
              opacity: 0.6,
            }}
          >
            {weightedRankChange < 0 ? "▼" : "▲"}
            {changeStr}
          </div>
        </div>
      </div>
    );
  };

  const calcWeightedRank = (data, isPrevWeek = false) => {
    const RankDays = 7; // assuming weightedRankDays is defined somewhere
    let weightedRankDays = RankDays + 1; // ignore today
    let startIdx = isPrevWeek ? -(2 * weightedRankDays) : -weightedRankDays;
    let endIdx = isPrevWeek ? -weightedRankDays : -1;
    const slicedWeightedRank = data?.slice(startIdx, endIdx);
    const sumWeightedRank = slicedWeightedRank?.reduce((a, b) => a + b, 0);
    return sumWeightedRank / (slicedWeightedRank?.length || 1);
  };

  const calcWeightedRankChange = (data) => {
    const weightedRank = calcWeightedRank(data);
    const prevWeightedRank = calcWeightedRank(data, true);
    const weightedRankChange =
      ((weightedRank - prevWeightedRank) /
        (prevWeightedRank === 0 ? 1 : prevWeightedRank)) *
      100;
    return weightedRankChange.toFixed(2);
  };

  const velocityDays = 7;
  const showVelocityChange = (data) => {
    const velocity = calcVelocity(data);
    const velocityChange = calcVelocityChange(data);
    const vlChangeStr =
      velocityChange === Infinity || isNaN(velocityChange)
        ? 0
        : Math.abs(velocityChange).toFixed(0) + "%";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          // marginLeft: "12px",
          marginTop: "5px",
        }}
      >
        <div style={{ color: "black" }}>{velocity.toFixed(2)}</div>
        <div style={{ color: "black", fontSize: 12, marginLeft: 2 }}>
          <div
            style={{
              color: velocityChange < 0 ? "red" : "green",
              display: "inline-block",
              opacity: 0.6,
            }}
          >
            {velocityChange < 0 ? "▼" : "▲"}
            {vlChangeStr}
          </div>
        </div>
      </div>
    );
  };

  const calcVelocity = useCallback((data, isPrevWeek = false) => {
    let velocity = 0;
    const velocityDays = 7; // assuming velocityDays is defined somewhere
    let velDays = velocityDays + 1; // ignore today
    let startIdx = isPrevWeek ? -(2 * velDays) : -velDays;
    let endIdx = isPrevWeek ? -velDays : -1;

    if (data) {
      if ("units" in data) {
        // child
        const slicedUnits = data.units.slice(startIdx, endIdx);
        const sumUnits = slicedUnits.reduce((a, b) => a + b, 0);
        velocity = sumUnits / (slicedUnits.length || 1);
      } else {
        // parent
        Object.values(data).forEach((value) => {
          velocity += calcVelocity(value, isPrevWeek);
        });
      }
    }
    return velocity;
  }, []);

  const calcVelocityChange = useCallback(
    (data) => {
      const velocity = calcVelocity(data);
      const prevVelocity = calcVelocity(data, true);
      const velocityChange =
        ((velocity - prevVelocity) / (prevVelocity === 0 ? 1 : prevVelocity)) *
        100;
      return velocityChange.toFixed(2);
    },
    [calcVelocity]
  );

  const calcPPC = useCallback((data, isPrevWeek = false) => {
    let ppc = 0;
    const ppcDays = 7; // assuming velocityDays is defined somewhere
    let velDays = ppcDays + 1; // ignore today
    let startIdx = isPrevWeek ? -(2 * velDays) : -velDays;
    let endIdx = isPrevWeek ? -velDays : -1;

    if (data && data instanceof Object) {
      if ("ppc" in data) {
        // child
        const slicedPPC = data.ppc.slice(startIdx, endIdx);
        ppc = slicedPPC.reduce((a, b) => a + b, 0) / ppcDays;
      } else {
        // parent
        Object.values(data).forEach((value) => {
          ppc += calcPPC(value, isPrevWeek);
        });
      }
    }
    return ppc;
  }, []);

  const calcPPCChange = useCallback(
    (data) => {
      const PPC = calcPPC(data);
      const prevPPC = calcPPC(data, true);
      const PPCChange = ((PPC - prevPPC) / (prevPPC === 0 ? 1 : prevPPC)) * 100;
      return PPCChange.toFixed(2);
    },
    [calcPPC]
  );

  const showPPCChange = (data) => {
    const ppc = calcPPC(data);
    const PPCChange = calcPPCChange(data);
    const changeStr =
      PPCChange === Infinity || isNaN(PPCChange)
        ? 0
        : Math.abs(PPCChange).toFixed(0) + "%";
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{ color: "black" }}>
          {getCurrencySymbol()}
          {convertCurrency(ppc.toFixed(2))}
        </div>
        <div style={{ color: "black", fontSize: 12, marginLeft: 2 }}>
          <div
            style={{
              color: PPCChange < 0 ? "green" : "red",
              display: "inline-block",
              opacity: 0.6,
            }}
          >
            {PPCChange < 0 ? "▼" : "▲"}
            {changeStr}
          </div>
        </div>
      </div>
    );
  };

  const showProfitChange = (data) => {
    const profit = calcProfit(data);
    const profitChange = calcProfitChange(data);
    const vlChangeStr =
      profitChange === Infinity || isNaN(profitChange)
        ? 0
        : Math.abs(profitChange).toFixed(0) + "%";
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{ color: "black" }}>
          {(profit < 0 ? `-${getCurrencySymbol()}` : getCurrencySymbol()) +
            convertCurrency(Math.abs(profit).toFixed(2))}
        </div>
        <div style={{ color: "black", fontSize: 12, marginLeft: 2 }}>
          <div
            style={{
              color: profitChange < 0 ? "red" : "green",
              display: "inline-block",
              opacity: 0.6,
            }}
          >
            {profitChange < 0 ? "▼" : "▲"}
            {vlChangeStr}
          </div>
        </div>
      </div>
    );
  };

  // calc profit out of the data.breakdown where Extra fees and Refunds are smoothed out
  const calcProfit = useCallback((data, isPrevWeek = false) => {
    let profit = 0;
    const smoothingDays = 30;
    const profitDays = 7; // assuming profitDays is defined somewhere
    let profDays = profitDays + 1; // ignore today
    let startIdx = isPrevWeek ? -(2 * profDays) : -profDays;
    let endIdx = isPrevWeek ? -profDays : -1;

    const profitStrValueArray = Object.values(data).slice(3);
    const smoothingWindow = profitStrValueArray.slice(0, smoothingDays);
    // the avg daily of the extra fees and refunds over the period
    const [smoothedRefunds, smoothedExtraFees] = smoothingWindow.reduce(
      (acc, moneyStr) => {
        const breakdown = moneyStr.breakdown;
        if (!breakdown) return acc;
        return [
          acc[0] + (breakdown["Refunds"] || 0) / (smoothingWindow.length || 1),
          acc[1] +
            (breakdown["Extra fees"] || 0) / (smoothingWindow.length || 1),
        ];
      },
      [0, 0]
    );

    const profitFloatArray = profitStrValueArray.map((moneyStr) => {
      const breakdown = moneyStr.breakdown;
      if (!breakdown) return 0;

      return Object.entries(breakdown).reduce((profit, [key, value]) => {
        switch (key) {
          case "Gross Sales":
            return profit + value;
          case "Refunds (Smoothed)":
            return profit - smoothedRefunds;
          case "Refunds (actual)":
            // This is already included in the breakdown, so just subtract it normally
            return profit - value;
          case "Extra fees":
            return profit + smoothedExtraFees;
          // VAT now falls through to the default (profit - value): Gross Sales
          // already has VAT added back in, so subtracting it here nets back to
          // the same net-of-VAT revenue the profit calc always used — same
          // result as before, just walked down from a real gross figure.
          // Returned Cogs is not included in the profit breakdown
          // case "Returned Cogs":
          //   return profit + value;
          default:
            return profit - value;
        }
      }, 0);
    });

    if (profitFloatArray.length > 0) {
      // Get the slice of days we want to analyze
      const slicedProfits = profitFloatArray.slice(startIdx, endIdx);
      // Calculate average daily profit for the period
      const sumProfits = slicedProfits.reduce((a, b) => a + b, 0);
      profit = sumProfits / (slicedProfits.length || 1);
    }

    return profit;
  }, []);

  // const calcRegularProfit = useCallback((data, isPrevWeek = false) => {
  //   let profit = 0;
  //   const profitDays = 7; // assuming profitDays is defined somewhere
  //   let profDays = profitDays + 1; // ignore today
  //   let startIdx = isPrevWeek ? -(2 * profDays) : -profDays;
  //   let endIdx = isPrevWeek ? -profDays : -1;

  //   const profitStrValueArray = Object.values(data).slice(3);
  //   const profitFloatArray = profitStrValueArray.map((moneyStr) =>
  //     parseFloat(moneyStr.value.replace(/[$,]/g, ""))
  //   );

  //   if (profitFloatArray.length > 0) {
  //     // child
  //     const slicedUnits = profitFloatArray.slice(startIdx, endIdx);
  //     const sumUnits = slicedUnits.reduce((a, b) => a + b, 0);
  //     profit = sumUnits / (slicedUnits.length || 1);
  //   }
  //   return profit;
  // }, []);

  const calcProfitChange = useCallback(
    (data) => {
      const profit = calcProfit(data);
      const prevProfit = calcProfit(data, true);
      // Handle negative numbers in percentage change calculation
      const profitChange =
        prevProfit === 0
          ? profit > 0
            ? 100
            : -100
          : ((profit - prevProfit) / Math.abs(prevProfit)) * 100;

      return profitChange.toFixed(2);
    },
    [calcProfit]
  );
  const calcTotalLTSF = useCallback(() => {
    let totalFees = 0;
    let totalUnits = 0;
    let currentFees = 0;
    let currentUnits = 0;
    let skusDict = {};

    if (longTermStorageData) {
      for (const asin in longTermStorageData) {
        Object.assign(skusDict, longTermStorageData[asin]);
      }

      for (const sku in skusDict) {
        currentFees = skusDict[sku]["estimated_cost"].reduce(
          (partialSum, a) => partialSum + a,
          0
        );
        currentUnits = skusDict[sku]["quantity"].reduce(
          (partialSum, a) => partialSum + a,
          0
        );
        totalFees += currentFees;
        totalUnits += currentUnits;
      }

      return {
        units: totalUnits,
        fees:
          totalFees % 1 === 0 ? totalFees : parseFloat(totalFees.toFixed(2)),
      };
    } else {
      return null;
    }
  }, [longTermStorageData]);

  const isAnyDateLessThan15Days = (shipped_eta) => {
    const currentDate = new Date();
    return shipped_eta.some((dateString) => {
      const date = new Date(dateString);
      const diffTime = Math.abs(date - currentDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 15;
    });
  };

  const getRemainingDaysInMonth = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const timeDifference = lastDayOfMonth.getTime() - today.getTime();
    const remainingDays = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    return remainingDays;
  };

  const calcLTSFRecommendation = useCallback(() => {
    const findOldestTierIndex = (skus) => {
      for (let i = 6; i >= 0; i--) {
        for (const sku of Object.values(skus)) {
          if (sku.quantity[i] > 0) {
            return i;
          }
        }
      }
      return 0;
    };

    const calcAsinLTSFRecommendations = (skus) => {
      let unitsSum = 0;
      let costSum = 0;
      const oldestTierIndex = findOldestTierIndex(skus);
      for (const sku of Object.values(skus)) {
        unitsSum += sku.quantity[oldestTierIndex];
        costSum += sku.estimated_cost[oldestTierIndex];
      }
      let daysTillEndOfMonth = getRemainingDaysInMonth();
      const velocity = calcVelocity(salesData);
      const unitSalesUntilEndOfMonth = Math.ceil(velocity * daysTillEndOfMonth);
      const velocityIncrease =
        (unitsSum - unitSalesUntilEndOfMonth) / daysTillEndOfMonth;
      return {
        neededUnitSales: unitsSum,
        possibleSavings: costSum,
        neededVelocityIncrease: velocityIncrease,
      };
    };

    let recommendations = {
      neededUnitSales: 0,
      possibleSavings: 0,
      neededVelocityIncrease: 0,
    };
    if (longTermStorageData && longTermStorageData[product.child_asin]) {
      recommendations = calcAsinLTSFRecommendations(
        longTermStorageData[product.child_asin]
      );
      recommendations.possibleSavings = Number(
        recommendations.possibleSavings.toFixed(2)
      );
      recommendations.neededVelocityIncrease = Number(
        recommendations.neededVelocityIncrease.toFixed(2)
      );
    }
    return recommendations;
  }, [calcVelocity, salesData, longTermStorageData, product.child_asin]);

  const LTSFRecommendation = useMemo(
    () => (product.children ? undefined : calcLTSFRecommendation()),
    [product.children, calcLTSFRecommendation]
  );

  const totalLTSF = useMemo(() => calcTotalLTSF(), [calcTotalLTSF]);

  const calcRealisticStock = useCallback(() => {
    const isOnTheWayRelevant = isAnyDateLessThan15Days(shipped_eta);
    const isInWorkInShippedRelevant =
      store === "rushmorerose" && shipped_qty === 0;
    const realisticStock =
      quantityfbaAvailable +
      (quantityfbaReserved - (quantityCustomerOrders || 0)) +
      quantityfbaInReceiving +
      quantity3pl +
      quantityAWD +
      quantityAWDOutbound +
      (isOnTheWayRelevant ? shipped_qty : 0) +
      (isInWorkInShippedRelevant ? inWorkInShipped_qty : 0);
    return realisticStock;
  }, [
    shipped_eta,
    quantityfbaAvailable,
    quantityfbaReserved,
    quantityCustomerOrders,
    quantityfbaInReceiving,
    quantity3pl,
    quantityAWD,
    quantityAWDOutbound,
    shipped_qty,
    inWorkInShipped_qty,
    store,
  ]);

  const calcPredictedDaysOfSupply = useCallback((asinFeaturesData) => {
    let predictedDaysOfSupply = null;
    if (asinFeaturesData && Object.keys(asinFeaturesData).length === 1) {
      asinFeaturesData = asinFeaturesData[Object.keys(asinFeaturesData)[0]];
    }

    predictedDaysOfSupply =
      asinFeaturesData &&
      asinFeaturesData.AdjustedDaysOfSupplyWithAwdFallback !== undefined
        ? asinFeaturesData.AdjustedDaysOfSupplyWithAwdFallback
        : null;

    if (predictedDaysOfSupply && predictedDaysOfSupply >= 60) {
      predictedDaysOfSupply = asinFeaturesData.AdjustedDaysOfSupply;
    }

    return predictedDaysOfSupply;
  }, []);

  const calcDaysOfSupply = useCallback(() => {
    let daysOfSupply = 0;
    let velocity = 0;
    const realisticStock = calcRealisticStock(
      shipped_eta,
      quantityfbaAvailable,
      quantityfbaReserved,
      quantityfbaInReceiving,
      quantity3pl,
      quantityAWD,
      quantityAWDOutbound,
      shipped_qty,
      inWorkInShipped_qty
    );

    velocity = calcVelocity(salesData);

    if (marketplace === "ca") {
      return Math.round(asinInventoryInCA.val / velocity);
    }

    daysOfSupply =
      velocity !== 0 && realisticStock !== 0
        ? Math.round(realisticStock / velocity)
        : realisticStock === 0
        ? 0
        : Infinity;

    let actionableDaysOfSupply = daysOfSupply;
    if (daysOfSupply >= 60) {
      let actionableStock = realisticStock - quantityAWD - quantity3pl;
      actionableDaysOfSupply = Math.round(actionableStock / velocity);
    }

    return actionableDaysOfSupply;
  }, [
    shipped_eta,
    quantityfbaAvailable,
    quantityfbaReserved,
    quantityfbaInReceiving,
    quantity3pl,
    quantityAWD,
    quantityAWDOutbound,
    shipped_qty,
    calcVelocity,
    salesData,
    calcRealisticStock,
    asinInventoryInCA,
    marketplace,
    inWorkInShipped_qty,
  ]);

  const [open, setOpen] = useState(openAllRows);
  const [isShown, setIsShown] = useState(true);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // Scroll sync (both axes) across this row's family: a parent row's grid and
  // the grids of all its children (the parent creates the family, children
  // receive it). The grid the user scrolls writes every other on-screen grid
  // directly, inside its own scroll event, so they all move in the same frame.
  // Each write is a real DOM scroll and makes that grid fire its own scroll
  // event next frame; the follower recognises the echo (the position it was
  // left at, clamped or not) and drops it, so nothing is ever written back to
  // the grid the user is holding and a grid with a shorter scroll range just
  // stops at its own end. The previous wiring relayed a child's scroll through
  // the parent grid's own scroll event: siblings moved 1-2 frames late, the
  // parent's stale position landed back on the child between two wheel ticks
  // (the child kept jumping back and lost about half the wheel input), and
  // the member with the shortest range dragged the whole family to its limit.
  // Every write also re-renders all of a grid's cells synchronously once its
  // position crosses the render window (GRID_ROW_BUFFER / GRID_COLUMN_BUFFER),
  // so a grid follows only while its row is on (or near) the screen; an
  // off-screen row remembers the last position and applies it when it scrolls
  // into view, which also aligns children that were collapsed (display:none
  // drops scroll writes) once their parent opens.
  const ownScrollFamilyRef = useRef(null);
  if (!scrollFamily && !ownScrollFamilyRef.current) {
    ownScrollFamilyRef.current = createScrollFamily();
  }
  const family = scrollFamily || ownScrollFamilyRef.current;
  const rowElementRef = useRef(null);
  const onScreenRef = useRef(true);
  const pendingScrollRef = useRef(null);
  const memberRef = useRef(null);
  if (!memberRef.current) {
    memberRef.current = { lastWritten: null, follow: null };
  }
  const member = memberRef.current;
  // Reassigned every render so it sees the current filter state without the
  // family having to re-register.
  member.follow = (params) => {
    if (isFilterActive) return;
    // Off screen, or no grid yet (it is only mounted while the row is near the
    // viewport): keep the position and apply it once the grid is there and
    // visible. Where the grid actually landed (it may have clamped) is what
    // the scroll event this write fires reports, and that is dropped as an
    // echo.
    const landed = onScreenRef.current
      ? scrollGridTo(apiRef.current, params)
      : null;
    if (!landed) {
      pendingScrollRef.current = { left: params.left, top: params.top };
      return;
    }
    member.lastWritten = landed;
  };

  // The heavy part of a row (product image, velocity chart, the daily data
  // grid and the rank modal) exists only while the row is on or near the
  // screen. The row itself, with its name, ASIN, SKU, metrics and inventory
  // numbers, stays mounted for every product so Cmd+F, the filters and
  // expand-all keep covering the whole store, while a store with thousands of
  // products holds only a screenful of grids and images in memory at a time.
  const gridAreaRef = useRef(null);
  // Height of the grid area the last time it was mounted, so the placeholder
  // that stands in for it keeps the row the same size and the page never
  // jumps as rows go in and out of the zone.
  const gridAreaHeightRef = useRef(null);
  const isNearViewport = useNearViewport(rowElementRef, {
    // Rows just outside the viewport are mounted and kept in sync too, so
    // scrolling the page never reveals a grid mid-mount or mid-catch-up.
    rootMargin: "800px 0px",
    onChange: (isNear) => {
      onScreenRef.current = isNear;
      if (isNear) {
        if (pendingScrollRef.current) {
          const pending = pendingScrollRef.current;
          pendingScrollRef.current = null;
          member.follow(pending);
        }
      } else if (gridAreaRef.current?.offsetHeight) {
        gridAreaHeightRef.current = gridAreaRef.current.offsetHeight;
      }
    },
  });
  const showGrid = isNearViewport && !isFilterActive;

  useEffect(() => {
    // Join the family and relay this grid's own scrolls to it, leaving both on
    // cleanup. Re-runs when the grid (un)mounts with the filter or the
    // viewport: MUI clears apiRef.current on unmount and a remount is a fresh
    // api with no listeners.
    const api = apiRef.current;
    if (!api?.subscribeEvent) return undefined;
    const leave = family.register(member);
    const unsubscribe = api.subscribeEvent(
      "scrollPositionChange",
      (params, event) => {
        if (!event?.target) {
          // Not a scroll but the grid announcing its position: once it has
          // its size after mounting, and again on resize. The grid mounts
          // without its scrolling element (that comes with the size), so this
          // is where a position kept while there was nothing to scroll gets
          // applied.
          const pending = pendingScrollRef.current;
          if (pending) {
            pendingScrollRef.current = null;
            member.follow(pending);
          }
          return;
        }
        const written = member.lastWritten;
        if (
          written &&
          isNear(written.left, params.left) &&
          isNear(written.top, params.top)
        ) {
          // Echo of our own write. Kept, not cleared: the browser can report
          // the same programmatic move twice when the writes straddle a frame.
          return;
        }
        member.lastWritten = null;
        family.broadcast(member, params);
      }
    );
    // A grid that has just (re)mounted starts at zero: bring it to wherever
    // the family is (a position broadcast while it was away, or the last one
    // the family saw). The grid mounts before its scrolling element exists,
    // so this queues the position for the announcement handled above.
    const catchUp = pendingScrollRef.current || family.lastPosition;
    if (catchUp) {
      pendingScrollRef.current = null;
      member.follow(catchUp);
    }
    return () => {
      unsubscribe();
      leave();
    };
  }, [apiRef, family, member, isFilterActive, showGrid]);

  // Showing or hiding the upcoming days adds or removes columns at the start
  // of the scrolling area: go back to it, so they come into view on show and
  // TODAY is first again on hide. Every grid of the family does this itself.
  const upcomingToggledRef = useRef(false);
  useEffect(() => {
    if (!upcomingToggledRef.current) {
      upcomingToggledRef.current = true;
      return undefined;
    }
    const frame = requestAnimationFrame(() =>
      member.follow({
        left: 0,
        top: getGridScroller(apiRef.current)?.scrollTop ?? 0,
      })
    );
    return () => cancelAnimationFrame(frame);
  }, [apiRef, member, upcomingShown]);

  // Where the handle goes: right after the "field" and "Total/Avg (30d)"
  // columns, read from the grid so a resized column moves it too.
  const [upcomingHandleLeft, setUpcomingHandleLeft] = useState(200);
  useEffect(() => {
    const api = apiRef.current;
    if (!hasUpcoming || !api?.getColumn || !api?.subscribeEvent) {
      return undefined;
    }
    const measure = () => {
      const width = ["field", "total"].reduce(
        (sum, field) => sum + (api.getColumn(field)?.computedWidth ?? 0),
        0
      );
      if (width > 0) setUpcomingHandleLeft(width);
    };
    measure();
    // A resize fires columnWidthChange. The columns are rebuilt on every
    // render, which resets a resized width without that event, so re-measure
    // on columnsChange too.
    const unsubscribers = ["columnWidthChange", "columnsChange"].map((event) =>
      api.subscribeEvent(event, measure)
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    // Like the family registration above, this follows the grid in and out of
    // existence with the filter and the viewport.
  }, [apiRef, hasUpcoming, isFilterActive, showGrid]);

  const toggleOpenStates = (newState) => {
    trackEvent("product_row_expand", {
      expanded: newState,
      asin: product.children ? product.parent_asin : product.child_asin,
      store,
    });
    setOpen(newState);
  };

  let allCalculatedValues = useMemo(() => {
    // Determine the ASIN to check for recommendations
    const asinForRecommendation = product.children
      ? product.parent_asin
      : product.child_asin;
    const ownHasVisibleRecommendation =
      ruleEngineDecisionsData &&
      getVisibleDecisionsForAsin(
        ruleEngineDecisionsData[asinForRecommendation],
        asinForRecommendation,
        saveRuleEngineResponseStatus
      ).length > 0;
    // A parent row is shown when it (or any of its children) has a visible
    // recommendation icon, so mirror that here for the "Has recommendation" filter.
    const childHasVisibleRecommendation =
      product.children &&
      ruleEngineDecisionsData &&
      product.children.some(
        (child) =>
          getVisibleDecisionsForAsin(
            ruleEngineDecisionsData[child.child_asin],
            child.child_asin,
            saveRuleEngineResponseStatus
          ).length > 0
      );
    const hasRecommendation =
      ownHasVisibleRecommendation || childHasVisibleRecommendation
        ? "yes"
        : "no";

    return {
      "Days of supply": calcDaysOfSupply(salesData),
      "Unit economics": 0,
      "Realistic stock": calcRealisticStock(),
      "Product velocity": calcVelocity(salesData),
      "Velocity change": parseFloat(calcVelocityChange(salesData)),
      Status: product.status || "",
      "Has recommendation": hasRecommendation,
    };
  }, [
    salesData,
    calcDaysOfSupply,
    calcRealisticStock,
    calcVelocity,
    calcVelocityChange,
    product.status,
    product.children,
    product.parent_asin,
    product.child_asin,
    ruleEngineDecisionsData,
    saveRuleEngineResponseStatus,
  ]);

  const runFilters = useCallback(() => {
    if (filters.length === 0) {
      return true;
    }

    for (const filter of filters) {
      switch (filter.action) {
        case "greater than":
          if (allCalculatedValues[filter.attr] <= filter.value) {
            return false;
          }
          break;
        case "less than":
          if (allCalculatedValues[filter.attr] >= filter.value) {
            return false;
          }
          break;
        case "equal to":
          if (allCalculatedValues[filter.attr] !== filter.value) {
            return false;
          }
          break;
        case "is":
          // String/tag comparison (case-insensitive)
          if (
            String(allCalculatedValues[filter.attr]).toLowerCase() !==
            String(filter.value).toLowerCase()
          ) {
            return false;
          }
          break;
        default:
          break;
      }
    }
    return true;
  }, [filters, allCalculatedValues]);

  useEffect(() => {
    const isParentWithChildren = leaf !== "yes" && !!product.children;
    if (isParentWithChildren) {
      setIsShown(false);
      let anyFilterActive = filters.length > 0;
      setIsFilterActive(anyFilterActive);
    } else {
      let filtersResults = runFilters();
      setIsShown(filtersResults);
      if (leaf === "yes" && filtersResults === true) {
        setTimeout(() => {
          setParentShow(true);
        }, 0);
      }
    }
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    setOpen(openAllRows);
  }, [openAllRows]);

  const showSalesDataTable = true;
  // leaf !== "yes"
  //   ? salesData &&
  //     Object.values(salesData).some((entry) => entry["units"].length > 0)
  //   : salesData && salesData["units"].length > 0;

  const unitEconomicsCalculationCompleted = (result) => {
    allCalculatedValues["Unit economics"] = result;
  };

  // Check if any children have rule engine decisions
  const hasChildrenWithRuleEngineDecisions = () => {
    if (leaf === "yes" || !product.children) {
      return false;
    }
    if (!isAtomicUser(user?.email)) {
      return false;
    }

    return product.children.some((child) => {
      return (
        getVisibleDecisionsForAsin(
          ruleEngineDecisionsData[child.child_asin],
          child.child_asin,
          saveRuleEngineResponseStatus
        ).length > 0
      );
    });
  };

  const shouldShowNotificationIcon =
    hasChildrenWithRuleEngineDecisions() && !open;

  const isInactive =
    (leaf === "yes" && product.status === "Inactive") ||
    (!product.children && product.status === "Inactive") ||
    (product.children &&
      product.children.length === 1 &&
      product.children[0].status === "Inactive");

  return (
    // render
    <>
      <div
        ref={rowElementRef}
        style={{ display: isShown ? "block" : "none" }}
        data-asin={product.children ? product.parent_asin : product.child_asin}
      >
        <TableRow
          style={{
            padding: 0,
            borderWidth: 1,
            backgroundColor:
              leaf !== "yes" && open && product.children
                ? "#E6E9F0"
                : "#ffffff",
            position: "relative", // Add relative positioning for the floating icon
            verticalAlign: "top",
            opacity: isInactive ? 0.35 : 1,
            transition: "opacity 0.3s ease",
          }}
        >
          {isAtomicUser(user?.email) &&
            (() => {
              const asin = product.children
                ? product.parent_asin
                : product.child_asin;
              const currentPrice =
                unitEconomicsData && asin in unitEconomicsData
                  ? unitEconomicsData[asin].price
                  : unitEconomicsData?.price;

              if (!ruleEngineDecisionsData[asin]) {
                return null;
              }

              const decisionData = ruleEngineDecisionsData[asin];

              // Use MultiDecisionWithIcon to handle both single and multiple decisions
              return (
                <MultiDecisionWithIcon
                  decisions={decisionData}
                  asin={asin}
                  sku={product.sku}
                  store={store}
                  marketplace={marketplace}
                  currentPrice={currentPrice}
                />
              );
            })()}

          {shouldShowNotificationIcon && (
            <div
              style={{
                position: "absolute",
                top: 8,
                left: 8,
                zIndex: 10,
              }}
            >
              <Tooltip title="One or more children have rule engine suggestions. Click to expand and view.">
                <IconButton
                  aria-label="expand to view rule engine suggestions"
                  size="small"
                  onClick={() => {
                    toggleOpenStates(true);
                    const firstChild = product.children?.find(
                      (child) => ruleEngineDecisionsData[child.child_asin]
                    );
                    if (firstChild) {
                      setTimeout(() => {
                        const el = document.querySelector(
                          `[data-asin="${firstChild.child_asin}"]`
                        );
                        el?.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                      }, 300);
                    }
                  }}
                  sx={{
                    backgroundColor: "transparent",
                    color: "#666666",
                    "&:hover": {
                      backgroundColor: "rgba(102, 102, 102, 0.04)",
                    },
                    width: 28,
                    height: 28,
                    "@keyframes pulse": {
                      "0%": {
                        transform: "scale(1)",
                      },
                      "50%": {
                        transform: "scale(1.35)",
                      },
                      "100%": {
                        transform: "scale(1)",
                      },
                    },
                  }}
                >
                  <CustomSuggestionIcon color="#666666" size={22} />
                </IconButton>
              </Tooltip>
            </div>
          )}

          {leaf !== "yes" ? (
            product.children ? ( // parent with children
              <TableCell
                sx={{ width: 20 }}
                style={{ paddingRight: 0, verticalAlign: "middle" }}
              >
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => toggleOpenStates(!open)}
                >
                  {open ? (
                    <KeyboardArrowDownIcon />
                  ) : (
                    <KeyboardArrowRightIcon />
                  )}
                </IconButton>
              </TableCell>
            ) : (
              <TableCell sx={{ width: 50 }} /> // parent no children
            )
          ) : (
            <TableCell
              sx={{ width: 100, backgroundColor: "#E6E9F0", minWidth: 100 }}
            /> // child
          )}
          <TableCell style={{ minWidth: "540px" }}>
            <div ref={inventoryCellRef}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: !product.children
                    ? leaf === "yes"
                      ? "410px" //child
                      : "480px" //parent no children
                    : "445px", //parent with children
                  // overflow: "hidden",
                }}
              >
                {isNearViewport ? (
                  <ProductThumbnail
                    src={
                      product.image ||
                      "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM="
                    }
                    alt={sku}
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 5,
                      flexShrink: 0,
                      backgroundColor: "#f1f5f9",
                    }}
                  />
                )}
                <div
                  style={{
                    marginLeft: 10,
                    whiteSpace: "nowrap",
                    marginBottom: -5,
                  }}
                >
                  <p
                    data-testid="product_name"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Link
                      data-testid="product_href"
                      href={
                        leaf === "yes" || domainExtension === ".ca" // in CA parent links are not available
                          ? `https://www.amazon${domainExtension}/dp/${product.child_asin}?th=1`
                          : `https://www.amazon${domainExtension}/dp/${product.parent_asin}?th=1`
                      }
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: 14,
                        textDecoration: "none",
                      }}
                      target="_blank"
                    >
                      {product.product_name?.substring(0, charMax)}
                    </Link>
                    {/* Inactive badge for child items, parents without children, or parents with single inactive child */}
                    {(leaf === "yes" && product.status === "Inactive") ||
                    (!product.children && product.status === "Inactive") ||
                    (product.children &&
                      product.children.length === 1 &&
                      product.children[0].status === "Inactive") ? (
                      <span
                        style={{
                          backgroundColor: "#e2e8f0",
                          color: "#64748b",
                          fontWeight: 600,
                          fontSize: 10,
                          borderRadius: 12,
                          padding: "2px 8px",
                          border: "1px solid #e2e8f0",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontFamily: "Inter",
                        }}
                      >
                        Inactive
                      </span>
                    ) : null}
                  </p>
                  <p
                    style={{
                      fontFamily: "Inter",
                      fontWeight: 400,
                      color: "#64748B",
                      fontSize: 12,
                      display:
                        leaf === "yes" || !product.children ? "flex" : "none",
                      alignItems: "center",
                      marginTop:
                        leaf === "yes" || !product.children ? -10 : "auto",
                      marginBottom: leaf === "yes" ? "auto" : 10,
                    }}
                    data-testid="product_sku"
                  >
                    <Link
                      data-testid="product_href"
                      href={`/advanced-business-report?level=sku&store=${store}&value=${product.sku}&start_date=${compareConditions.current.start_date}&end_date=${compareConditions.current.end_date}&periodicity=day&metrics=units,avg_price`}
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: 14,
                        textDecoration: "none",
                        marginRight: 1,
                      }}
                      target="_blank"
                    >
                      {product.children ? "" : "SKU:"}
                    </Link>
                    {product.children ? "" : " " + sku}
                  </p>
                  <p
                    style={{
                      fontFamily: "Inter",
                      fontWeight: 400,
                      color: "#64748B",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      marginTop: leaf !== "yes" ? -8 : 0,
                    }}
                    data-testid="product_asin"
                  >
                    <Link
                      data-testid="product_href"
                      href={`/advanced-business-report?level=${
                        leaf === "yes" ? "child" : "parent"
                      }_asin&store=${store}&value=${
                        leaf === "yes"
                          ? product.child_asin
                          : product.parent_asin
                      }&start_date=${
                        compareConditions.current.start_date
                      }&end_date=${
                        compareConditions.current.end_date
                      }&periodicity=day&metrics=units,avg_price`}
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: 14,
                        textDecoration: "none",
                        marginRight: 1,
                      }}
                      target="_blank"
                    >
                      {leaf === "yes" ? "ASIN: " : "PARENT: "}
                    </Link>
                    {leaf === "yes"
                      ? " " + product.child_asin?.substring(0, charMax)
                      : " " + product.parent_asin?.substring(0, charMax)}
                    {leaf !== "yes" ? (
                      <div style={{ marginLeft: 15, fontStyle: "italic" }}>
                        <b>{(inventoryData[0]?.rating * 10).toFixed(1)}</b> ⭐
                        {" (" + inventoryData[0]?.reviews + ")"}
                      </div>
                    ) : (
                      <></>
                    )}
                    {bsrData && bsrData.categories && (
                      <BSRDisplay
                        bsrData={bsrData.ranks}
                        categories={bsrData.categories}
                        marketplace={marketplace}
                      />
                    )}
                    <div style={{ marginLeft: 15, marginTop: 3 }}>
                      <Link
                        data-testid="product_slack_href"
                        href={`slack://channel?team=T03E7NZR801&id=${slackChannelPerBrand(
                          store
                        )}`}
                        onClick={() => {
                          trackEvent("slack_link_clicked", {
                            asin:
                              leaf === "yes"
                                ? product.child_asin
                                : product.parent_asin,
                            store,
                          });
                          navigator.clipboard.writeText(
                            "SKU: " +
                              sku +
                              (leaf === "yes"
                                ? " Child: " + product.child_asin
                                : " Parent: " + product.parent_asin)
                          );
                        }}
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 600,
                          fontSize: 14,
                          textDecoration: "none",
                        }}
                        target="_blank"
                      >
                        <SiSlack
                          style={{
                            width: "15px",
                            height: "15px",
                            color: "#4A154B",
                          }}
                        />
                      </Link>
                    </div>
                  </p>
                </div>
              </div>
              {isAtomicUser(user?.email) && (
                <FionaInProgressChip
                  decisions={
                    ruleEngineDecisionsData?.[
                      product.children
                        ? product.parent_asin
                        : product.child_asin
                    ]
                  }
                />
              )}
              <div style={{ margin: "8px 0" }} />
              {!isFilterActive && (
                <InventoryDetails
                  product={product}
                  store={store}
                  quantity3pl={quantity3pl}
                  quantityAWD={quantityAWD}
                  quantityAWDInbound={quantityAWDInbound}
                  quantityAWDOutbound={quantityAWDOutbound}
                  quantityfbaAvailable={quantityfbaAvailable}
                  quantityfbaReserved={quantityfbaReserved}
                  quantityfbaInReceiving={quantityfbaInReceiving}
                  quantityfbaInWork={quantityfbaInWork}
                  shipped_qty={shipped_qty}
                  shipped_eta={shipped_eta}
                  inWorkInShipped_qty={inWorkInShipped_qty}
                  totalLTSF={totalLTSF}
                  LTSFRecommendation={LTSFRecommendation}
                  quantityCustomerOrders={quantityCustomerOrders}
                  quantityProcessing={quantityProcessing}
                  quantityTransfer={quantityTransfer}
                  asinInventoryInCA={asinInventoryInCA}
                  marketplace={marketplace}
                  deliveryTimesData={deliveryTimesData}
                  openAllInventory={openAllInventory}
                  awdInboundEta={awdInboundEta}
                />
              )}
            </div>
          </TableCell>
          <TableCell
            sx={{
              fontFamily: "Inter",
              fontWeight: 300,
              color: "#475569",
              fontSize: 12,
              minWidth: leaf === "yes" ? 202 : 234,
              textAlign: "left",
              paddingLeft: leaf === "yes" ? "10px" : "60px",
            }}
          >
            <DailyMetrics
              product={product}
              salesData={salesData}
              profitabilityData={profitabilityData}
              showPPCChange={showPPCChange}
              showProfitChange={showProfitChange}
              showVelocityChange={showVelocityChange}
              tableProfitDataForProduct={tableProfitDataForProduct}
              showWeightedRankChange={showWeightedRankChange}
              calcDaysOfSupply={calcDaysOfSupply}
              calcPredictedDaysOfSupply={calcPredictedDaysOfSupply}
              velocityDays={velocityDays}
              isFilterActive={isFilterActive}
              leaf={leaf}
              marketplace={marketplace}
              asinFeaturesData={asinFeaturesData}
              store={store}
            />
          </TableCell>
          <TableCell
            sx={{
              fontFamily: "Inter",
              fontWeight: 300,
              color: "#475569",
              fontSize: 12,
              minWidth: leaf === "yes" ? 204 : 214,
              textAlign: "left",
              paddingLeft: leaf === "yes" ? "11px" : "25px",
            }}
          >
            <div style={{ display: isFilterActive ? "none" : "block" }}>
              {!product.children ? "" : "Avg "}Unit Economics
              <Tooltip
                title={
                  <>
                    {!product.children
                      ? ""
                      : "Average of all child variations - "}
                    Numbers refer to latest date in the selected period
                    <br />
                    PPC per unit is - (total PPC / total Units sold) in the last
                    7 days
                    <br />
                  </>
                }
              >
                <InfoIcon
                  sx={{
                    marginLeft: "3px",
                    width: "14px",
                    height: "14px",
                    color: "#CBD5E1",
                  }}
                />
              </Tooltip>
              <div>
                <UnitEconomics
                  unitEconomicsData={unitEconomicsData}
                  onCalculationComplete={unitEconomicsCalculationCompleted}
                  ppcPerUnit={calcPpcPerUnit(
                    leaf === "yes" ? [salesData] : Object.values(salesData)
                  )}
                  marketplace={marketplace}
                  asin={
                    !product.children
                      ? product.child_asin
                      : product.children.length === 1
                      ? product.children[0].child_asin
                      : null
                  }
                  store={store}
                />
              </div>
            </div>
          </TableCell>
          <TableCell
            sx={{
              fontFamily: "Inter",
              fontWeight: 300,
              color: "#475569",
              fontSize: 14,
              paddingLeft: leaf === "yes" ? "0px" : "7px",
            }}
          >
            {isFilterActive ? (
              <div style={{ width: "55vw", minHeight: "20px" }} />
            ) : !isNearViewport ? (
              <Skeleton
                variant="rectangular"
                animation={false}
                data-testid="product_grid_placeholder"
                sx={{ bgcolor: "#f8fafc" }}
                style={{
                  width: "55vw",
                  height: `${
                    gridAreaHeightRef.current || inventoryCellHeight
                  }px`,
                }}
              />
            ) : (
              showSalesDataTable && (
                <div ref={gridAreaRef}>
                  <VelocityWindowSummary
                    predictedUnitsData={predictedUnitsData}
                    upcomingDays={upcomingDates.length}
                    upcomingTargetUnits={upcomingTargetUnits}
                    upcomingShown={upcomingShown}
                    onToggleUpcoming={toggleUpcomingDays}
                  />
                  <div style={{ position: "relative" }}>
                    {hasUpcoming && (
                      <UpcomingDaysTab
                        days={upcomingDates.length}
                        targetUnits={upcomingTargetUnits}
                        shown={upcomingShown}
                        pulse={shouldPulse(upcomingSeen)}
                        left={upcomingHandleLeft}
                        onClick={toggleUpcomingDays}
                      />
                    )}
                    <DataTable
                      disableColumnReorder={true}
                      rowReordering={false}
                      apiRef={apiRef}
                      rowHeight={35}
                      columnHeaderHeight={35}
                      style={{
                        backgroundColor: "white",
                        height: `${inventoryCellHeight}px`,
                        width: "55vw",
                      }}
                      columns={tableColumnsForProduct(
                        columnsSalesData,
                        leaf,
                        leaf === "yes"
                          ? product.child_asin
                          : product.parent_asin,
                        store,
                        comments,
                        marketplace,
                        compareConditions?.current?.start_date,
                        compareConditions?.current?.end_date,
                        convertCurrency,
                        getCurrencySymbol,
                        upcomingShown ? upcomingDates : [],
                        resolveWeightedRankSeasonality(
                          seasonalityData,
                          salesData,
                          leaf,
                          product
                        ),
                        hasUpcoming
                      )}
                      rows={getTableDailyData(
                        salesData,
                        profitabilityData,
                        ranksData,
                        predictedUnitsData,
                        externalQuantity,
                        leaf,
                        store,
                        productRanksLoading,
                        convertCurrency,
                        getCurrencySymbol
                      )}
                      getRowId={(row) => row.id}
                      getRowClassName={(params) =>
                        params.row.field === "rank" ? "rank-row" : ""
                      }
                      pinnedColumns={{
                        left: [
                          "field",
                          "total",
                          ...(hasUpcoming ? [UPCOMING_HANDLE_FIELD] : []),
                        ],
                      }}
                      hideFooterRowCount
                      rowBuffer={GRID_ROW_BUFFER}
                      rowThreshold={GRID_ROW_BUFFER}
                      columnBuffer={GRID_COLUMN_BUFFER}
                      columnThreshold={GRID_COLUMN_BUFFER}
                      onCellClick={handleCellClick}
                    />
                  </div>
                </div>
              )
            )}
          </TableCell>
        </TableRow>
        {leaf !== "yes" && product.children ? (
          <Collapse
            in={true}
            timeout="auto"
            style={{ display: open ? "block" : "none" }}
          >
            {product.children.map((child, index) => (
              <ProductRowDetailed
                key={index}
                store={store}
                sku={inventoryData
                  .filter((x) => x.asin === child.child_asin)
                  .map((x) => x.sku)
                  .join(", ")}
                product={child}
                compareConditions={compareConditions}
                leaf={"yes"}
                openAllRows={openAllRows}
                openAllInventory={openAllInventory}
                inventoryData={inventoryData.filter(
                  (x) => child.child_asin === x.asin
                )}
                salesData={salesData[child.child_asin]}
                unitEconomicsData={unitEconomicsData[child.child_asin]}
                profitabilityData={profitabilityData[child.child_asin]}
                ranksData={ranksData[child.child_asin]}
                predictedUnitsData={
                  predictedUnitsData[child.child_asin]
                    ? {
                        [child.child_asin]:
                          predictedUnitsData[child.child_asin],
                      }
                    : {}
                }
                longTermStorageData={{
                  [child.child_asin]: longTermStorageData[child.child_asin]
                    ? longTermStorageData[child.child_asin]
                    : null,
                }}
                deliveryTimesData={(deliveryTimesData || []).filter(
                  (item) => item.child_asin === child.child_asin
                )}
                ruleEngineDecisionsData={ruleEngineDecisionsData}
                asinFeaturesData={asinFeaturesData[child.child_asin]}
                comments={comments}
                filters={filters}
                setParentShow={setIsShown}
                scrollFamily={family}
                upcomingFamily={{
                  dates: upcomingDates,
                  shown: upcomingShown,
                  setShown: setUpcomingShown,
                }}
                marketplace={marketplace}
                bsrData={bsrData}
                seasonalityData={seasonalityData}
              />
            ))}
          </Collapse>
        ) : null}
        <TableRow>
          <TableCell style={{ padding: 0, borderBottom: 0 }} colSpan={12} />
        </TableRow>
        {(isNearViewport || openModal) && (
          <RankTrackingModalPopup
            openModal={openModal}
            handleCloseModal={handleCloseModal}
            asin={!product.children ? product.child_asin : product.parent_asin}
            leaf={!product.children}
            currentStore={store}
            currentStartDate={compareConditions.current.start_date}
            currentEndDate={compareConditions.current.end_date}
            marketplace={marketplace}
            weightedRankData={
              leaf === "yes"
                ? salesData
                : Object.keys(salesData || {}).length === 1
                ? Object.values(salesData || {})[0]
                : null
            }
          />
        )}
      </div>
    </>
  );
};

export {
  amazonInboundBeyondAwd,
  formatSalesCellTooltip,
  getFutureTargetDates,
  getTableDailyData,
  getVisibleDecisionsForAsin,
  ProductRowDetailed,
  resolveWeightedRankSeasonality,
};
